import React, { useState, useEffect, useRef, createRef, useCallback, useMemo } from "react";
import * as pdfjsLib from "pdfjs-dist";
import { Task, ManageUser as MU, getEmail, File as IzFile } from "@induz/sdk";
import Drawer from "components/Drawer/index";
import "./pdfViewer.scss";
import Toolbar from "./Toolbar";
import Thumbnails from "./Thumbnails";
import SlidesView from "./SlidesView";
import logo from "../../assets/images/homepage/main/logo.svg";
import logoDark from "../../assets/images/homepage/main/logoDark.svg";
import useTheme from "hooks/observer";
import filterIcon from "../../assets/images/pdf_viewer/filter.svg";
import filterIconDark from "../../assets/images/pdf_viewer/filter_dark_mode.svg";
import markResolvedIcon from "../../assets/images/pdf_viewer/mark_as_resolved.svg";
import markResolvedIconDark from "../../assets/images/pdf_viewer/mark_as_resolved_dark_mode.svg";
import { useComments } from "./useComment";
import { Avatar } from "components/Avatar/index";
import PDFDrawToolbar from "./PDFDrawToolbar";
import PDFDrawCanvas from "./PDFDrawCanvas";
import { useToast } from "./Toast";
import Modal from "components/Models/index";

// Set up PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js";

// Helper function to calculate time ago
const getTimeAgo = (date) => {
  const now = new Date();
  const diffInSeconds = Math.floor((now - date) / 1000);

  if (diffInSeconds < 60) {
    return `${diffInSeconds}s ago`;
  } else if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return `${minutes}m ago`;
  } else if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return `${hours}h ago`;
  } else if (diffInSeconds < 604800) {
    const days = Math.floor(diffInSeconds / 86400);
    return `${days}d ago`;
  } else {
    return date.toLocaleDateString();
  }
};

// Helper to calculate caret coordinates in a textarea
const getCaretCoordinates = (element, position) => {
  const { scrollLeft, scrollTop } = element;
  const style = window.getComputedStyle(element);

  const mirror = document.createElement("div");
  const mirrorStyle = mirror.style;

  mirrorStyle.position = "absolute";
  mirrorStyle.visibility = "hidden";
  mirrorStyle.whiteSpace = "pre-wrap";
  mirrorStyle.wordWrap = "break-word";

  const properties = [
    "direction", "boxSizing", "width", "height", "overflowX", "overflowY",
    "borderTopWidth", "borderRightWidth", "borderBottomWidth", "borderLeftWidth", "borderStyle",
    "paddingTop", "paddingRight", "paddingBottom", "paddingLeft",
    "fontStyle", "fontVariant", "fontWeight", "fontStretch", "fontSize", "fontSizeAdjust", "lineHeight", "fontFamily",
    "textAlign", "textTransform", "textIndent", "textDecoration", "letterSpacing", "wordSpacing", "tabSize", "MozTabSize"
  ];

  properties.forEach(prop => {
    mirrorStyle[prop] = style[prop];
  });

  const text = element.value.substring(0, position);
  mirror.textContent = text;

  const span = document.createElement("span");
  span.textContent = element.value.substring(position, position + 1) || ".";
  mirror.appendChild(span);

  document.body.appendChild(mirror);
  const top = span.offsetTop - scrollTop;
  const left = span.offsetLeft - scrollLeft;
  document.body.removeChild(mirror);

  return { top, left };
};

// Drawing export quality setting (0.0 to 1.0)
const DRAWING_EXPORT_QUALITY = 0.9;

// LRU Cache implementation for chunk caching
class LRUCache {
  constructor(maxSize = 50 * 1024 * 1024) { // 50MB default
    this.cache = new Map();
    this.maxSize = maxSize;
    this.currentSize = 0;
  }

  get(key) {
    if (this.cache.has(key)) {
      // Move to end (most recently used)
      const value = this.cache.get(key);
      this.cache.delete(key);
      this.cache.set(key, value);
      return value;
    }
    return undefined;
  }

  set(key, value) {
    const size = value.byteLength || value.length || 0;

    // Remove existing entry if present
    if (this.cache.has(key)) {
      const oldValue = this.cache.get(key);
      const oldSize = oldValue.byteLength || oldValue.length || 0;
      this.currentSize -= oldSize;
      this.cache.delete(key);
    }

    // Evict least recently used entries if needed
    while (this.currentSize + size > this.maxSize && this.cache.size > 0) {
      const firstKey = this.cache.keys().next().value;
      const firstValue = this.cache.get(firstKey);
      const firstSize = firstValue.byteLength || firstValue.length || 0;
      this.cache.delete(firstKey);
      this.currentSize -= firstSize;
    }

    // Add new entry
    if (size <= this.maxSize) {
      this.cache.set(key, value);
      this.currentSize += size;
    }
  }

  has(key) {
    return this.cache.has(key);
  }

  delete(key) {
    if (this.cache.has(key)) {
      const value = this.cache.get(key);
      const size = value.byteLength || value.length || 0;
      this.cache.delete(key);
      this.currentSize -= size;
    }
  }

  clear() {
    this.cache.clear();
    this.currentSize = 0;
  }

  get size() {
    return this.cache.size;
  }
}

const PDFViewer = ({ isVisible, handleCancel, fo, fileName, dupCurrentPath, fileOwner = [], onSaveComplete }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(1.2);
  const [rotation, setRotation] = useState(0);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSlidesMode, setIsSlidesMode] = useState(false);
  const [renderKey, setRenderKey] = useState(0);
  const [commentMenuOpen, setCommentMenuOpen] = useState(null); // Track which comment's menu is open
  const [replyMenuOpen, setReplyMenuOpen] = useState(null); // Track which reply's menu is open: { commentId, replyIndex }
  const [editingCommentId, setEditingCommentId] = useState(null); // Track which comment is being edited
  const [editingReply, setEditingReply] = useState(null); // Track which reply is being edited: { commentId, replyIndex }
  const [editCommentText, setEditCommentText] = useState(""); // Text for editing comment
  const [editReplyText, setEditReplyText] = useState(""); // Text for editing reply
  const [commentFilter, setCommentFilter] = useState("all"); // "all" or "pagewise"
  const [statusFilter, setStatusFilter] = useState({ active: true, resolved: false }); // Track both active and resolved selections
  const [filterMenuOpen, setFilterMenuOpen] = useState(false); // Track if filter menu is open
  const [isDrawing, setIsDrawing] = useState(false); // Whether user is drawing a selection area
  const [selectionStart, setSelectionStart] = useState(null); // { page, x, y }
  const [selectionEnd, setSelectionEnd] = useState(null); // { page, x, y }
  const [sidebarWidth, setSidebarWidth] = useState(320); // Width of comments sidebar
  const [isCommentsSidebarOpen, setIsCommentsSidebarOpen] = useState(false); // Comments sidebar open state
  const [isResizing, setIsResizing] = useState(false); // Track if sidebar is being resized
  const [expandedComments, setExpandedComments] = useState({}); // Track expanded/collapsed state for long comments
  const [highlightedCommentId, setHighlightedCommentId] = useState(null); // Track which comment is highlighted
  const [allUsers, setAllUsers] = useState([]); // List of all users in the organization
  const [mentionState, setMentionState] = useState({
    active: false,
    query: "",
    startIndex: -1,
    targetInput: null, // "comment", "reply", "edit"
    commentId: null, // For reply/edit, which comment we're mentioning in
    coords: { top: 0, left: 0 }
  });
  const [isEditMode, setIsEditMode] = useState(false); // PDF drawing/edit mode
  const [isPreparingEditMode, setIsPreparingEditMode] = useState(false); // Track if edit mode is being prepared
  const [selectedDrawTool, setSelectedDrawTool] = useState("pen"); // "pen", "highlighter"
  const [selectedDrawColor, setSelectedDrawColor] = useState("#000000"); // Selected color for drawing
  const [fitScale, setFitScale] = useState(1); // Scale required to fit width
  const [brushSize, setBrushSize] = useState(25); // Brush size for pen and highlighter
  const [isSaving, setIsSaving] = useState(false); // Track save operation
  const [saveProgress, setSaveProgress] = useState(0); // Save progress percentage
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false); // Track if there are unsaved drawings
  const [hoveredComment, setHoveredComment] = useState(null); // Track hovered comment for tooltip
  const drawingsChangeTimeoutRef = useRef(null); // For debouncing drawing updates
  const saveAbortRef = useRef(false); // Abort flag for cancelling an in-progress save
  const [showCloseConfirm, setShowCloseConfirm] = useState(false); // Show confirmation dialog
  const [closeAction, setCloseAction] = useState(null); // Track what to close: "draw" or "pdf"
  const drawCanvasRef = useRef(null); // Ref to PDFDrawCanvas to access drawings
  const latestPdfBytesRef = useRef(null); // Latest PDF bytes including previously saved drawings (used for multi-save without losing prior saves)
  const showToast = useToast();

  // Permission helper functions
  const getCurrentUserEmail = useCallback(() => {
    try {
      const currentUserMail = getEmail()?.toLowerCase() || "";
      return currentUserMail;
    } catch (error) {
      console.error("Failed to get current user email:", error);
      return "";
    }
  }, []);

  const getFileOwnerEmail = useCallback(() => {
    try {
      const FileOwner = fileOwner?.toLowerCase() || "";
      return FileOwner;
    } catch (error) {
      console.error("Failed to get file owner email:", error);
      return "";
    }
  }, [fo]);

  // Check if user can edit comment/reply (only owner can edit)
  const canEditComment = useCallback((commentEmail) => {
    const currentUser = getCurrentUserEmail();
    const commentOwner = (commentEmail || "").toLowerCase();
    return currentUser === commentOwner;
  }, [getCurrentUserEmail]);

  // Check if user can delete comment/reply (owner or file owner can delete)
  const canDeleteComment = useCallback((commentEmail) => {
    const currentUser = getCurrentUserEmail();
    const fileOwner = getFileOwnerEmail();
    const commentOwner = (commentEmail || "").toLowerCase();
    return currentUser === commentOwner || currentUser === fileOwner;
  }, [getCurrentUserEmail, getFileOwnerEmail]);

  // Fetch users for mentions
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const userList = await MU.listUsers();
        if (Array.isArray(userList)) {
          // Store full user objects to show name and email in dropdown
          setAllUsers(userList.map(u => ({
            name: u.name || "",
            email: u.email || ""
          })));
        }
      } catch (err) {
        console.error("Failed to fetch users for mentions", err);
      }
    };
    fetchUsers();
  }, []);

  const filteredUsersForMention = useMemo(() => {
    if (!mentionState.active) return [];
    if (!mentionState.query) return allUsers;
    const query = mentionState.query.toLowerCase();
    return allUsers.filter(u =>
      u.email.toLowerCase().includes(query) ||
      u.name.toLowerCase().includes(query)
    );
  }, [mentionState.active, mentionState.query, allUsers]);

  const handleMentionSelect = (user) => {
    const { startIndex, targetInput } = mentionState;
    const email = user.email;
    if (targetInput === "comment") {
      const before = commentText.substring(0, startIndex);
      const after = commentText.substring(startIndex + mentionState.query.length + 1);
      setCommentText(`${before}@${email} ${after}`);
    } else if (targetInput === "reply") {
      const before = replyText.substring(0, startIndex);
      const after = replyText.substring(startIndex + mentionState.query.length + 1);
      setReplyText(`${before}@${email} ${after}`);
    } else if (targetInput === "edit") {
      const before = editCommentText.substring(0, startIndex);
      const after = editCommentText.substring(startIndex + mentionState.query.length + 1);
      setEditCommentText(`${before}@${email} ${after}`);
    } else if (targetInput === "editReply") {
      const before = editReplyText.substring(0, startIndex);
      const after = editReplyText.substring(startIndex + mentionState.query.length + 1);
      setEditReplyText(`${before}@${email} ${after}`);
    }
    setMentionState({ active: false, query: "", startIndex: -1, targetInput: null, commentId: null, coords: { top: 0, left: 0 } });
  };

  const renderMentionDropdown = (type, currentCommentId = null) => {
    if (!mentionState.active || mentionState.targetInput !== type) return null;
    if (currentCommentId !== null && mentionState.commentId !== currentCommentId) return null;

    // Use a slightly larger offset for reply and edit inputs in the sidebar
    const topOffset = (type === "reply" || type === "edit" || type === "editReply") ? 25 : 20;

    // Calculate dropdown position with viewport boundary checks
    const calculateDropdownPosition = () => {
      // Estimate dropdown dimensions (typical dropdown size)
      const dropdownWidth = 280; // Approximate width of dropdown
      const dropdownHeight = Math.min(300, filteredUsersForMention.length * 60 + 20); // Max 5 items visible, ~60px per item

      // Get the input element to calculate absolute position
      let inputElement = null;

      // Try to find the active/focused input first
      const activeElement = document.activeElement;
      if (activeElement && (
        activeElement.classList.contains("pdf-comment-panel-input") ||
        activeElement.classList.contains("pdf-comments-sidebar-reply-textarea") ||
        activeElement.classList.contains("pdf-comments-sidebar-edit-textarea")
      )) {
        inputElement = activeElement;
      } else {
        // Fallback to querySelector based on type
        if (type === "comment") {
          inputElement = document.querySelector(".pdf-comment-panel-input");
        } else if (type === "reply") {
          inputElement = document.querySelector(".pdf-comments-sidebar-reply-textarea");
        } else if (type === "edit" || type === "editReply") {
          // For edit, try to find the specific one based on commentId if available
          if (mentionState.commentId !== null) {
            // Find the parent comment item and then the textarea within it
            const commentItem = document.querySelector(`[data-comment-id="${mentionState.commentId}"]`);
            if (commentItem) {
              inputElement = commentItem.querySelector(".pdf-comments-sidebar-edit-textarea");
            }
          }
          // Fallback to first edit textarea if not found
          if (!inputElement) {
            inputElement = document.querySelector(".pdf-comments-sidebar-edit-textarea");
          }
        }
      }

      if (!inputElement) {
        // Fallback to original coordinates if input not found
        return {
          top: mentionState.coords.top + topOffset,
          left: mentionState.coords.left
        };
      }

      // Get input element's position relative to viewport
      const inputRect = inputElement.getBoundingClientRect();

      // Calculate absolute position of caret
      const absoluteTop = inputRect.top + mentionState.coords.top + topOffset;
      const absoluteLeft = inputRect.left + mentionState.coords.left;

      // Check viewport boundaries and adjust
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const padding = 10; // Padding from viewport edges

      let finalTop = absoluteTop;
      let finalLeft = absoluteLeft;

      // Check horizontal overflow (right side)
      if (absoluteLeft + dropdownWidth + padding > viewportWidth) {
        // Position to the left of caret if it would overflow right
        finalLeft = Math.max(padding, absoluteLeft - dropdownWidth);
      } else if (absoluteLeft - padding < 0) {
        // Ensure minimum padding from left edge
        finalLeft = padding;
      }

      // Check vertical overflow (bottom)
      if (absoluteTop + dropdownHeight + padding > viewportHeight) {
        // Position above the caret if it would overflow bottom
        finalTop = Math.max(padding, absoluteTop - dropdownHeight - topOffset);
      } else if (absoluteTop - padding < 0) {
        // Ensure minimum padding from top edge
        finalTop = padding;
      }

      return {
        top: finalTop,
        left: finalLeft
      };
    };

    const position = calculateDropdownPosition();

    const dropdownStyle = {
      top: `${position.top}px`,
      left: `${position.left}px`,
      position: "fixed",
      zIndex: 2000
    };

    return (
      <div className="pdf-mention-dropdown" data-theme={theme} style={dropdownStyle}>
        <div className="pdf-mention-dropdown-list">
          {filteredUsersForMention.length > 0 ? (
            filteredUsersForMention.map((user) => {
              const displayName = user.name || user.email.split("@")[0];
              const initial = displayName.charAt(0).toUpperCase();
              return (
                <div
                  key={user.email}
                  className="pdf-mention-item"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMentionSelect(user);
                  }}
                >
                  <div className="pdf-mention-item-avatar">
                    <Avatar initial={initial} size={36} />
                  </div>
                  <div className="pdf-mention-item-info">
                    <div className="pdf-mention-item-name">{displayName}</div>
                    <div className="pdf-mention-item-email">{user.email}</div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="pdf-mention-no-users">No users found</div>
          )}
        </div>
      </div>
    );
  };

  const handleInputChangeWithMentions = (e, type, commentId = null) => {
    const value = e.target.value;
    const cursorIndex = e.target.selectionStart;

    // Update the corresponding text state
    if (type === "comment") setCommentText(value);
    else if (type === "reply") setReplyText(value);
    else if (type === "edit") setEditCommentText(value);
    else if (type === "editReply") setEditReplyText(value);

    // Detect "@" trigger
    const textBeforeCursor = value.substring(0, cursorIndex);
    const lastAtIndex = textBeforeCursor.lastIndexOf("@");

    if (lastAtIndex !== -1) {
      const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
      // Check if there's a space between @ and cursor
      if (!textAfterAt.includes(" ")) {
        const coords = getCaretCoordinates(e.target, lastAtIndex);
        setMentionState({
          active: true,
          query: textAfterAt,
          startIndex: lastAtIndex,
          targetInput: type,
          commentId,
          coords
        });
        return;
      }
    }

    if (mentionState.active) {
      setMentionState({ active: false, query: "", startIndex: -1, targetInput: null, commentId: null, coords: { top: 0, left: 0 } });
    }
  };

  const renderTextWithMentions = (text) => {
    if (!text) return "";

    const parts = text.split(/(@[^\s]+)/g);
    return parts.map((part, i) => {
      if (part.startsWith("@")) {
        return <span key={i} className="mention">{part}</span>;
      }
      return part;
    });
  };

  // Handle sidebar resizing
  const handleResizerMouseDown = useCallback((e) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  const handleResize = useCallback((e) => {
    if (!isResizing) return;

    // Calculate new width based on mouse position from the right
    const newWidth = window.innerWidth - e.clientX;

    // Set boundaries for resizing (e.g., 200px to 600px)
    if (newWidth >= 250 && newWidth <= 600) {
      setSidebarWidth(newWidth);
    }
  }, [isResizing]);

  const stopResizing = useCallback(() => {
    setIsResizing(false);
  }, []);

  // Add global event listeners for resizing
  useEffect(() => {
    if (isResizing) {
      window.addEventListener("mousemove", handleResize);
      window.addEventListener("mouseup", stopResizing);
      // Change cursor globally during resizing
      document.body.style.cursor = "col-resize";
    } else {
      window.removeEventListener("mousemove", handleResize);
      window.removeEventListener("mouseup", stopResizing);
      document.body.style.cursor = "";
    }

    return () => {
      window.removeEventListener("mousemove", handleResize);
      window.removeEventListener("mouseup", stopResizing);
      document.body.style.cursor = "";
    };
  }, [isResizing, handleResize, stopResizing]);

  // Use comments hook for all comment-related functionality
  const {
    comments,
    setComments,
    isCommentMode,
    setIsCommentMode,
    pendingComment,
    setPendingComment,
    commentText,
    setCommentText,
    replyingToCommentId,
    setReplyingToCommentId,
    replyText,
    setReplyText,
    loadComments: loadCommentsFromBackend,
    saveComment: saveCommentToBackend,
    updateComment: updateCommentInBackend,
    deleteComment: deleteCommentFromBackend,
    addReply: addReplyToBackend,
    updateReply: updateReplyInBackend,
    deleteReply: deleteReplyFromBackend,
    getCurrentPageComments,
  } = useComments(fileName, dupCurrentPath, isVisible, isSlidesMode, fo);
  const canvasRefs = useRef([]);
  const thumbRefs = useRef([]);
  const pageRefs = useRef([]);
  const containerRef = useRef(null);
  const pagesContainerRef = useRef(null);
  const thumbsContainerRef = useRef(null);
  const currentPageRef = useRef(1);
  const preservedPageRef = useRef(null);
  const prevSlidesModeRef = useRef(false);
  const restoringPageRef = useRef(false);
  const isRenderedRef = useRef(false); // Track if all pages are rendered
  const commentTextareaRef = useRef(null); // Ref for comment textarea
  const [useLazyRendering, setUseLazyRendering] = useState(false); // Rendering strategy
  const [fileSize, setFileSize] = useState(0); // File size for complexity calculation
  const renderedPagesRef = useRef(new Set()); // Track which pages have been rendered (for lazy rendering)
  const mountedPagesRef = useRef(new Set()); // Track mounted pages for virtualization
  const observerRef = useRef(null); // IntersectionObserver ref
  const thumbnailObserverRef = useRef(null); // IntersectionObserver for thumbnails
  const renderedThumbnailsRef = useRef(new Set()); // Track rendered thumbnails
  const renderingThumbnailsRef = useRef(new Map()); // Track thumbnails currently being rendered: { pageNum: renderTask }
  const renderingPagesRef = useRef(new Map()); // Track pages currently being rendered: { pageNum: renderTask }
  const pngWorkerRef = useRef(null); // Web Worker for PNG conversion
  const workerTimeoutRef = useRef(null); // Timeout ref for Web Worker cleanup
  const theme = useTheme();

  const scrollToPage = useCallback((pageNum) => {
    const target = pageRefs.current[pageNum - 1]?.current;
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    setCurrentPage(pageNum);
  }, []);

  const handleToggleCommentMode = useCallback(() => {
    if (isEditMode) return; // Disable comments when draw mode is active
    setIsCommentMode((prev) => !prev);
  }, [isEditMode]);

  const handleToggleCommentsSidebar = useCallback(() => {
    if (isEditMode) return; // Disable sidebar toggle when draw mode is active
    setIsCommentsSidebarOpen((prev) => {
      const newState = !prev;
      // If closing the sidebar, also disable comment mode
      if (!newState) {
        setIsCommentMode(false);
        setPendingComment(null);
        setCommentText("");
      }
      return newState;
    });
  }, [isEditMode, setIsCommentMode, setPendingComment, setCommentText]);

  const handleMouseDown = useCallback(
    (e) => {
      if (!isCommentMode) return;

      const pageIndex = pageRefs.current.findIndex(
        (ref) => ref?.current && ref.current.contains(e.target)
      );
      if (pageIndex === -1) return;

      const pageEl = pageRefs.current[pageIndex].current;
      const canvas = pageEl.querySelector("canvas");
      if (!canvas) return;

      const rect = canvas.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width;
      const y = (e.clientY - rect.top) / rect.height;

      // Ensure click is within the canvas
      if (x < 0 || x > 1 || y < 0 || y > 1) return;

      const normX = Math.min(Math.max(x, 0), 1);
      const normY = Math.min(Math.max(y, 0), 1);

      setIsDrawing(true);
      setSelectionStart({ page: pageIndex + 1, x: normX, y: normY });
      setSelectionEnd({ page: pageIndex + 1, x: normX, y: normY });
    },
    [isCommentMode]
  );

  const handleMouseMove = useCallback(
    (e) => {
      if (!isCommentMode || !isDrawing || !selectionStart) return;

      const pageIndex = pageRefs.current.findIndex(
        (ref) => ref?.current && ref.current.contains(e.target)
      );

      if (pageIndex === -1) return;

      const pageEl = pageRefs.current[pageIndex].current;
      const canvas = pageEl.querySelector("canvas");
      if (!canvas) return;

      const rect = canvas.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width;
      const y = (e.clientY - rect.top) / rect.height;

      // Clamp coordinates to canvas boundaries
      const normX = Math.min(Math.max(x, 0), 1);
      const normY = Math.min(Math.max(y, 0), 1);

      if (selectionStart.page === pageIndex + 1) {
        setSelectionEnd({ page: pageIndex + 1, x: normX, y: normY });
      }
    },
    [isCommentMode, isDrawing, selectionStart]
  );

  const handleMouseUp = useCallback(
    (e) => {
      if (!isDrawing || !selectionStart || !selectionEnd) {
        setIsDrawing(false);
        return;
      }

      // Calculate final area
      const page = selectionStart.page;
      const x = Math.min(selectionStart.x, selectionEnd.x);
      const y = Math.min(selectionStart.y, selectionEnd.y);
      const width = Math.abs(selectionStart.x - selectionEnd.x);
      const height = Math.abs(selectionStart.y - selectionEnd.y);

      // Require a minimum selection size to add a comment (remove single pointing)
      if (width < 0.005 && height < 0.005) {
        setIsDrawing(false);
        setSelectionStart(null);
        setSelectionEnd(null);
        setIsCommentMode(false); // Optionally turn off comment mode
        return;
      }

      setPendingComment({
        page,
        x,
        y,
        width,
        height
      });

      setCommentText("");
      setIsCommentMode(false);
      setIsDrawing(false);
      setSelectionStart(null);
      setSelectionEnd(null);
    },
    [isDrawing, selectionStart, selectionEnd]
  );

  const scrollToCommentLocation = useCallback((comment) => {
    if (!comment) return;

    // Highlight the comment
    setHighlightedCommentId(comment.id);
    // Remove highlight after 2 seconds
    setTimeout(() => {
      setHighlightedCommentId(null);
    }, 2000);

    const pageIndex = comment.page - 1;
    const pageRef = pageRefs.current[pageIndex]?.current;
    if (!pageRef) {
      // If page not rendered yet, just scroll to page
      scrollToPage(comment.page);
      return;
    }

    const pageEl = pageRef;
    const canvas = pageEl.querySelector("canvas");
    if (!canvas) {
      scrollToPage(comment.page);
      return;
    }

    // First, scroll the page into view
    pageEl.scrollIntoView({ behavior: "smooth", block: "start" });
    setCurrentPage(comment.page);

    // Then, after a short delay to ensure the page is scrolled, adjust to show the comment
    setTimeout(() => {
      if (!pagesContainerRef.current) return;

      // Get the page element's position relative to the container
      const containerRect = pagesContainerRef.current.getBoundingClientRect();
      const pageRect = pageEl.getBoundingClientRect();

      // Calculate the offset of the page from the container top
      const pageOffsetTop = pageRect.top - containerRect.top + pagesContainerRef.current.scrollTop;

      // Get the canvas height to calculate absolute comment position
      const canvasHeight = canvas.offsetHeight;

      // Calculate the absolute y position of the comment (comment.y is a percentage 0-1)
      const commentY = comment.y * canvasHeight;

      // Calculate the target scroll position to show the comment near the top (with 100px offset)
      const targetScrollTop = pageOffsetTop + commentY - 100;

      // Scroll the pages container to show the comment
      pagesContainerRef.current.scrollTo({
        top: Math.max(0, targetScrollTop),
        behavior: "smooth"
      });
    }, 100); // Small delay to let scrollIntoView start
  }, [scrollToPage]);

  // Create custom PDFDataRangeTransport for true streaming
  const createStreamingTransport = (fo, fileName, dupCurrentPath) => {
    let totSize = 0;
    let path = "";
    const chunkCache = new LRUCache(50 * 1024 * 1024); // 50MB LRU cache for exact ranges
    const alignedChunkCache = new LRUCache(100 * 1024 * 1024); // 100MB LRU cache for aligned chunks
    const loadingPromises = new Map();
    const chunkSize = 4 * 1024 * 1024; // 4MB chunks (same as downloadIzFile)

    // Determine the path - EXACTLY like downloadIzFile
    const determinePath = () => {
      if (!path) {
        // Use the same path calculation as downloadIzFile
        path = fo.getParentPath() + "/" + fo.getName();
        if (dupCurrentPath.length === 1 && dupCurrentPath[0] === "shared" || dupCurrentPath[0] === "external_shared") {
          fileName = fo.getFileUuId();
          path = fo.getParentPath() + "/" + fileName;
        }
      }
      return path;
    };

    // Fetch a 4MB-aligned chunk from server (like downloadIzFile does)
    const fetchAlignedChunk = async (alignedOffset, filePath, viewTask) => {
      const alignedKey = alignedOffset;

      // Return cached aligned chunk if available
      if (alignedChunkCache.has(alignedKey)) {
        return alignedChunkCache.get(alignedKey);
      }

      // Return existing promise if already loading
      if (loadingPromises.has(`aligned-${alignedKey}`)) {
        return loadingPromises.get(`aligned-${alignedKey}`);
      }

      // Create promise to fetch aligned chunk
      const alignedPromise = (async () => {
        try {
          const remainingSize = Math.min(chunkSize, totSize - alignedOffset);

          const chunk = await fo.getChunk(alignedOffset, filePath, viewTask, remainingSize);

          // Cache the aligned chunk
          alignedChunkCache.set(alignedKey, chunk);
          return chunk;
        } catch (err) {
          showToast("Error fetching PDF", "error");
          throw err;
        } finally {
          loadingPromises.delete(`aligned-${alignedKey}`);
        }
      })();

      loadingPromises.set(`aligned-${alignedKey}`, alignedPromise);
      return alignedPromise;
    };

    // Custom PDFDataRangeTransport class
    class CustomPDFDataRangeTransport extends pdfjsLib.PDFDataRangeTransport {
      constructor(length) {
        super(length);
        this.length = length;
        totSize = length;
      }

      requestDataRange(begin, end) {
        // Use this.length (set in constructor) instead of closure variable
        const fileSize = this.length;

        // PDF.js uses EXCLUSIVE end (like array.slice: end is not included)
        // So range 0-65536 means bytes 0 through 65535 (65536 bytes total)
        // This is different from HTTP Range requests which use inclusive end

        // Validate the range first
        if (begin < 0 || begin >= fileSize) {
          console.error(`[PDF Stream] Invalid begin offset: ${begin} (file size: ${fileSize})`);
          // PDF.js will handle the error when data is not provided
          return;
        }

        // If end is beyond file size, clamp it to file size (exclusive, so fileSize is valid)
        let actualEnd = end;
        if (end > fileSize) {
          actualEnd = fileSize;
        }

        if (actualEnd <= begin) {
          console.error(`[PDF Stream] Invalid range: begin (${begin}) >= end (${actualEnd})`);
          // PDF.js will handle the error when data is not provided
          return;
        }

        const rangeKey = `${begin}-${actualEnd}`;

        // Return cached data if available
        if (chunkCache.has(rangeKey)) {
          const cachedData = chunkCache.get(rangeKey);
          this.onDataRange(begin, cachedData);
          return;
        }

        // Return if already loading
        if (loadingPromises.has(rangeKey)) {
          return;
        }

        // Create loading promise
        const loadPromise = (async () => {
          try {
            // Calculate actual range length (end is EXCLUSIVE, so no +1)
            // Range 0-65536 means bytes 0 through 65535 = 65536 bytes
            const rangeLength = actualEnd - begin;

            // Double-check rangeLength is valid
            if (rangeLength <= 0 || rangeLength > fileSize) {
              throw new Error(`Invalid range length: ${rangeLength} (file size: ${fileSize})`);
            }

            const filePath = determinePath();
            const viewTask = new Task(fileSize, fileName, "Viewing");

            // Fetch 4MB-aligned chunks and extract the exact range needed
            // This ensures we always request chunks the server likes (4MB-aligned)
            const startAlignedOffset = Math.floor(begin / chunkSize) * chunkSize;
            const endAlignedOffset = Math.floor((actualEnd - 1) / chunkSize) * chunkSize;

            // Fetch all aligned chunks we need
            const alignedChunks = [];
            for (let alignedOffset = startAlignedOffset; alignedOffset <= endAlignedOffset; alignedOffset += chunkSize) {
              const alignedChunk = await fetchAlignedChunk(alignedOffset, filePath, viewTask);
              alignedChunks.push({ offset: alignedOffset, data: alignedChunk });
            }

            // Combine aligned chunks into a continuous buffer
            const maxNeededOffset = Math.max(...alignedChunks.map(c => c.offset + c.data.length));
            const combinedBuffer = new Uint8Array(maxNeededOffset - startAlignedOffset);
            for (const { offset, data } of alignedChunks) {
              const relativeOffset = offset - startAlignedOffset;
              combinedBuffer.set(data, relativeOffset);
            }

            // Extract the exact range PDF.js requested
            const relativeBegin = begin - startAlignedOffset;
            const relativeEnd = relativeBegin + rangeLength;
            const actualChunk = combinedBuffer.slice(relativeBegin, relativeEnd);

            if (actualChunk.length !== rangeLength) {
              // If we got less, pad with zeros (shouldn't happen, but handle gracefully)
              if (actualChunk.length < rangeLength) {
                const padded = new Uint8Array(rangeLength);
                padded.set(actualChunk, 0);
                padded.fill(0, actualChunk.length);
                chunkCache.set(rangeKey, padded);
                this.onDataRange(begin, padded);
                return;
              }
            }

            // Cache the result
            chunkCache.set(rangeKey, actualChunk);

            // Notify PDF.js that data is ready
            // PDF.js expects: onDataRange(begin, chunk) where chunk is Uint8Array of exact size
            this.onDataRange(begin, actualChunk);
          } catch (err) {
            console.error(`[PDF Stream] Error loading range ${begin}-${actualEnd}:`, err);
            // PDFDataRangeTransport doesn't have onDataError method
            // PDF.js will handle the error when it doesn't receive the expected data
          } finally {
            loadingPromises.delete(rangeKey);
          }
        })();

        loadingPromises.set(rangeKey, loadPromise);
      }
    }

    return CustomPDFDataRangeTransport;
  };

  // Helper function to clean up cursor styles and drawing canvases
  const cleanupDrawMode = useCallback(() => {
    const container = pagesContainerRef.current || document.querySelector(".pdf-pages");
    if (!container) return;

    // Remove pdf-drawing-active class from all PDF pages
    const allPages = container.querySelectorAll(".pdf-page");
    allPages.forEach(page => {
      page.classList.remove("pdf-drawing-active");
    });
    container.classList.remove("pdf-drawing-active");

    // Remove all drawing canvas elements (they have cursor: none in CSS)
    const drawCanvases = container.querySelectorAll(".pdf-draw-canvas");
    drawCanvases.forEach(canvas => {
      if (canvas.parentNode) {
        canvas.parentNode.removeChild(canvas);
      }
    });

    // Reset body cursor if needed
    if (document.body.style.cursor === "none") {
      document.body.style.cursor = "";
    }
  }, []);

  // Helper function to reset draw state
  const resetDrawState = useCallback(() => {
    setSelectedDrawTool("pen");
    setSelectedDrawColor("#000000");
    setBrushSize(25);
    setHasUnsavedChanges(false);
  }, []);

  // Reset state when viewer is closed or fileName changes
  useEffect(() => {
    if (!isVisible) {
      // Clear all state when viewer is closed
      setPdfDoc(null);
      setTotalPages(0);
      setCurrentPage(1);
      setError(null);
      setLoading(true);
      setComments([]);
      setPendingComment(null);
      setCommentText("");
      setIsCommentMode(false);
      // Reset saved-bytes cache when closing viewer (avoid leaking between files)
      latestPdfBytesRef.current = null;

      // Clear drawing/edit mode state
      setIsEditMode(false);
      setIsPreparingEditMode(false);
      resetDrawState();
      setShowCloseConfirm(false);

      // Clear saved drawings since they're already stored in the database
      if (drawCanvasRef.current?.clearSavedDrawings) {
        drawCanvasRef.current.clearSavedDrawings();
      }

      // Clear debounce timeout
      if (drawingsChangeTimeoutRef.current) {
        clearTimeout(drawingsChangeTimeoutRef.current);
        drawingsChangeTimeoutRef.current = null;
      }

      // Clean up Web Worker and timeout
      if (workerTimeoutRef.current) {
        clearTimeout(workerTimeoutRef.current);
        workerTimeoutRef.current = null;
      }
      if (pngWorkerRef.current) {
        pngWorkerRef.current.terminate();
        pngWorkerRef.current = null;
      }

      // Clear mounted pages tracking
      mountedPagesRef.current.clear();

      // Clear canvases
      canvasRefs.current.forEach((ref) => {
        const canvas = ref?.current;
        if (canvas) {
          const context = canvas.getContext("2d");
          context.clearRect(0, 0, canvas.width, canvas.height);
          canvas.width = 0;
          canvas.height = 0;
        }
      });
    }
  }, [isVisible, fileName, resetDrawState]);

  // Reset drawing state when a new PDF is opened (fileName changes)
  useEffect(() => {
    if (isVisible && fileName) {
      // Reset drawing/edit mode state when opening a new PDF
      setIsEditMode(false);
      resetDrawState();
      // Reset saved-bytes cache when opening a new file
      latestPdfBytesRef.current = null;

      // Clear saved drawings when opening a new PDF (they're stored in the database)
      if (drawCanvasRef.current?.clearSavedDrawings) {
        drawCanvasRef.current.clearSavedDrawings();
      }
    }
  }, [fileName, isVisible, resetDrawState]);

  // Load PDF document from stream
  useEffect(() => {
    if (!isVisible || !fo) return;

    let loadingTask = null;
    let pdfDocument = null;

    const loadPDF = async (retryCount = 0) => {
      const maxRetries = 3;
      try {
        setLoading(true);
        setError(null);

        // Get file size first
        const totSize = await fo.getSize();
        setFileSize(totSize);

        // Create custom streaming transport class
        const TransportClass = createStreamingTransport(fo, fileName, dupCurrentPath);

        // Create transport instance with the file size
        const transport = new TransportClass(totSize);

        // Load PDF with custom transport for true streaming
        // PDF.js will call requestRange() as needed, allowing pages to render
        // as soon as enough data arrives (typically after first few KB)
        loadingTask = pdfjsLib.getDocument({
          range: transport,
          disableStream: false,
          disableAutoFetch: false,
          disableRange: false,
        });

        const pdf = await loadingTask.promise;
        pdfDocument = pdf;
        setPdfDoc(pdf);
        // Pre-build canvas refs before rendering so they exist on first paint
        const pages = pdf.numPages;
        canvasRefs.current = Array.from({ length: pages }, () => createRef());
        thumbRefs.current = Array.from({ length: pages }, () => createRef());
        pageRefs.current = Array.from({ length: pages }, () => createRef());
        setTotalPages(pages);
        setCurrentPage(1);
        setLoading(false);
        isRenderedRef.current = false; // Reset rendered state for new PDF

        // Load comments from backend using hook only if sidebar is open
        if (isCommentsSidebarOpen) {
          const response = await loadCommentsFromBackend();
          if (response.status === "changed") {
            setComments(response.comments);
          }
        }
      } catch (err) {
        console.error("PDF load error:", err);

        // Enhanced error handling with specific messages
        let errorMessage = "Failed to load PDF";
        let shouldRetry = false;

        if (err.name === "PasswordException" || err.message?.includes("password")) {
          errorMessage = "This PDF is password protected. Password support is not yet available.";
        } else if (err.name === "InvalidPDFException" || err.message?.includes("Invalid PDF")) {
          errorMessage = "Invalid or corrupted PDF file. Please check if the file is valid.";
        } else if (err.message?.includes("network") || err.message?.includes("fetch") || err.message?.includes("NetworkError")) {
          errorMessage = "Network error. Please check your connection and try again.";
          shouldRetry = retryCount < maxRetries;
        } else if (err.message?.includes("timeout") || err.message?.includes("aborted")) {
          errorMessage = "Request timed out. The file may be too large or the connection is slow.";
          shouldRetry = retryCount < maxRetries;
        } else if (err.message) {
          errorMessage = `Failed to load PDF: ${err.message}`;
          // Retry for generic errors if it might be network related
          shouldRetry = retryCount < maxRetries && retryCount < 2;
        }

        if (shouldRetry) {
          // Retry with exponential backoff
          const delay = 1000 * Math.pow(2, retryCount); // 1s, 2s, 4s
          setError(`${errorMessage} Retrying... (${retryCount + 1}/${maxRetries})`);

          await new Promise(resolve => setTimeout(resolve, delay));
          return loadPDF(retryCount + 1);
        } else {
          setError(errorMessage);
          setLoading(false);
        }
      }
    };

    loadPDF();

    // Cleanup on unmount or when dependencies change
    return () => {
      // Cancel loading task if still loading
      if (loadingTask) {
        loadingTask.destroy();
      }
      // Destroy PDF document to free memory
      if (pdfDocument) {
        pdfDocument.destroy();
      }
    };
  }, [isVisible, fo, fileName, dupCurrentPath]);

  // Poll comments every second for real-time updates (only when sidebar is open)
  useEffect(() => {
    if (!isVisible || !pdfDoc || !isCommentsSidebarOpen) return;

    const intervalId = setInterval(async () => {
      try {
        const response = await loadCommentsFromBackend();
        // Only update comments if status is "changed"
        if (response.status === "changed") {
          setComments(response.comments);
        }
      } catch (error) {
        console.error("Failed to refresh comments:", error);
      }
    }, 1000); // Poll every second

    return () => {
      clearInterval(intervalId);
    };
  }, [isVisible, pdfDoc, isCommentsSidebarOpen, loadCommentsFromBackend, setComments]);

  // Auto-focus comment textarea when pending comment is created
  useEffect(() => {
    if (pendingComment && commentTextareaRef.current) {
      // Use setTimeout to ensure the DOM is updated
      setTimeout(() => {
        commentTextareaRef.current?.focus();
      }, 0);
    }
  }, [pendingComment]);

  // Initialize Web Worker for PNG conversion
  useEffect(() => {
    if (typeof Worker !== 'undefined') {
      try {
        // Create Web Worker for PNG conversion using inline blob
        const workerCode = `
          self.onmessage = function(e) {
            const { canvasData, width, height } = e.data;
            try {
              const imageData = new ImageData(
                new Uint8ClampedArray(canvasData),
                width,
                height
              );
              const canvas = new OffscreenCanvas(width, height);
              const ctx = canvas.getContext('2d');
              ctx.putImageData(imageData, 0, 0);
              canvas.convertToBlob({ type: 'image/png' }).then(blob => {
                const reader = new FileReader();
                reader.onload = function() {
                  self.postMessage({ success: true, dataUrl: reader.result });
                };
                reader.onerror = function() {
                  self.postMessage({ success: false, error: 'Failed to read blob' });
                };
                reader.readAsDataURL(blob);
              }).catch(error => {
                self.postMessage({ success: false, error: error.message });
              });
            } catch (error) {
              self.postMessage({ success: false, error: error.message });
            }
          };
        `;
        const blob = new Blob([workerCode], { type: 'application/javascript' });
        const worker = new Worker(URL.createObjectURL(blob));
        pngWorkerRef.current = worker;

        return () => {
          if (worker) {
            worker.terminate();
            URL.revokeObjectURL(blob);
            pngWorkerRef.current = null;
          }
        };
      } catch (error) {
        console.warn('Failed to create PNG Web Worker, will use fallback:', error);
        pngWorkerRef.current = null;
      }
    }
  }, []);

  // Calculate rendering complexity and determine strategy
  const calculateRenderingComplexity = useCallback(async (pdfDoc, totalPages, scale, fileSizeBytes) => {
    try {
      // Quick check: sample first 3-5 pages to estimate complexity
      const sampleSize = Math.min(5, totalPages);
      let totalPageArea = 0;
      let maxPageArea = 0;

      for (let i = 1; i <= sampleSize; i++) {
        const page = await pdfDoc.getPage(i);
        const viewport = page.getViewport({ scale: 1, rotation: 0 });
        const pageArea = viewport.width * viewport.height;
        totalPageArea += pageArea;
        maxPageArea = Math.max(maxPageArea, pageArea);
      }

      const avgPageArea = totalPageArea / sampleSize;

      // Calculate complexity factors
      const pageCountFactor = totalPages;
      const dimensionFactor = avgPageArea / 1_000_000; // Normalize to millions
      const fileSizeFactor = fileSizeBytes / (1024 * 1024); // Convert to MB
      const scaleFactor = scale * scale; // Scale squared (area)

      // Combined complexity score
      // Weights: 30% page count, 40% page size, 20% file size, 10% scale
      const complexityScore =
        (pageCountFactor * 0.3) +
        (dimensionFactor * 50 * 0.4) +
        (fileSizeFactor * 0.2) +
        (scaleFactor * 0.1);

      return {
        score: complexityScore,
        pageCount: totalPages,
        avgPageArea,
        fileSizeMB: fileSizeFactor,
        useLazy: complexityScore >= 50 // Threshold for lazy rendering
      };
    } catch (error) {
      console.warn("Failed to calculate complexity, using page count fallback:", error);
      // Fallback to page count
      return {
        score: totalPages,
        pageCount: totalPages,
        avgPageArea: 0,
        fileSizeMB: 0,
        useLazy: totalPages >= 50
      };
    }
  }, []);

  // Determine rendering strategy when PDF loads
  useEffect(() => {
    if (!pdfDoc || !totalPages || !isVisible || isSlidesMode) return;

    const determineStrategy = async () => {
      const complexity = await calculateRenderingComplexity(pdfDoc, totalPages, scale, fileSize);
      setUseLazyRendering(complexity.useLazy);
      console.log(`Rendering strategy: ${complexity.useLazy ? 'Lazy' : 'Synchronous'} (Complexity: ${complexity.score.toFixed(2)})`);
    };

    determineStrategy();
  }, [pdfDoc, totalPages, scale, fileSize, isVisible, isSlidesMode, calculateRenderingComplexity]);

  // Render a single page (used by both strategies)
  const renderSinglePage = useCallback(async (pageNum) => {
    if (!pdfDoc) return false;

    try {
      // Cancel any existing render task for this page
      const existingTask = renderingPagesRef.current.get(pageNum);
      if (existingTask && existingTask.cancel) {
        existingTask.cancel();
      }

      const page = await pdfDoc.getPage(pageNum);
      const canvas = canvasRefs.current[pageNum - 1]?.current;

      if (!canvas) {
        // Retry if canvas not ready
        await new Promise(resolve => setTimeout(resolve, 100));
        const retryCanvas = canvasRefs.current[pageNum - 1]?.current;
        if (!retryCanvas) {
          console.warn(`Canvas for page ${pageNum} not found after retry`);
          return false;
        }
      }

      const context = canvas.getContext("2d");
      const viewport = page.getViewport({ scale, rotation });

      canvas.height = viewport.height;
      canvas.width = viewport.width;

      // Start render and store the task
      const renderTask = page.render({ canvasContext: context, viewport });
      renderingPagesRef.current.set(pageNum, renderTask);

      await renderTask.promise;

      // Mark as rendered and clear the task after successful render
      renderedPagesRef.current.add(pageNum);
      renderingPagesRef.current.delete(pageNum);

      // Thumbnail rendering is now handled by lazy loading (see thumbnail lazy loading effect)

      return true;
    } catch (error) {
      // Only log if it's not a cancellation error
      if (error.name !== "RenderingCancelledException") {
        console.error(`Error rendering page ${pageNum}:`, error);
      }
      // Clear the task on error
      renderingPagesRef.current.delete(pageNum);
      return false;
    }
  }, [pdfDoc, scale, rotation, isSidebarOpen]);

  // Build refs and render pages (synchronous or lazy based on complexity)
  useEffect(() => {
    if (!pdfDoc || !isVisible || totalPages === 0 || isSlidesMode) return;

    // Ensure refs are built for all pages
    if (canvasRefs.current.length !== totalPages) {
      canvasRefs.current = Array.from({ length: totalPages }, () => createRef());
    }
    if (thumbRefs.current.length !== totalPages) {
      thumbRefs.current = Array.from({ length: totalPages }, () => createRef());
    }

    let cancelled = false;

    // Synchronous rendering for low complexity PDFs
    const renderAllPages = async () => {
      try {
        // Wait a bit for DOM to be ready
        await new Promise(resolve => setTimeout(resolve, 100));

        // If sidebar just opened, force re-render thumbs
        if (isSidebarOpen) {
          thumbRefs.current.forEach((ref) => {
            const t = ref?.current;
            if (t) {
              t.dataset.rendered = "";
            }
          });
        }

        let renderedCount = 0;
        for (let pageNum = 1; pageNum <= totalPages && !cancelled; pageNum++) {
          const success = await renderSinglePage(pageNum);
          if (success) renderedCount++;
        }

        // Mark as rendered if all pages are done
        if (renderedCount === totalPages && !cancelled) {
          isRenderedRef.current = true;
        } else {
          isRenderedRef.current = false;
        }
      } catch (err) {
        if (!cancelled) {
          console.error("Page render error:", err);
          setError("Failed to render PDF page.");
        }
      }
    };

    // Lazy rendering with IntersectionObserver for high complexity PDFs
    const setupLazyRendering = () => {
      // Clean up previous observer
      if (observerRef.current) {
        observerRef.current.disconnect();
      }

      // Reset rendered pages tracking
      renderedPagesRef.current.clear();

      // Reset mounted pages tracking
      mountedPagesRef.current.clear();

      // Use ref for mounted pages to persist across renders
      const mountedPages = mountedPagesRef.current;

      // Create IntersectionObserver to render pages as they come into view
      observerRef.current = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const pageElement = entry.target;
            const pageNum = parseInt(pageElement.dataset.pageNum);

            if (entry.isIntersecting && !cancelled) {
              // Mount and render page when it comes into view
              if (!renderedPagesRef.current.has(pageNum)) {
                renderedPagesRef.current.add(pageNum);
                mountedPages.add(pageNum);

                // Ensure canvas exists
                const canvas = canvasRefs.current[pageNum - 1]?.current;
                if (canvas) {
                  renderSinglePage(pageNum).then((success) => {
                    if (success && observerRef.current) {
                      // Keep observing for unmounting when off-screen
                    }
                  });
                }
              }
            } else {
              // Unmount page when it goes off-screen (virtualization)
              // Keep a buffer of 2 pages above and below
              const currentPageNum = currentPageRef.current || 1;
              const buffer = 2;
              const shouldKeepMounted = Math.abs(pageNum - currentPageNum) <= buffer;

              if (!shouldKeepMounted && mountedPages.has(pageNum)) {
                // Clear canvas to free memory
                const canvas = canvasRefs.current[pageNum - 1]?.current;
                if (canvas) {
                  const ctx = canvas.getContext("2d");
                  ctx.clearRect(0, 0, canvas.width, canvas.height);
                  canvas.width = 0;
                  canvas.height = 0;
                }

                // Clear thumbnail
                const thumb = thumbRefs.current[pageNum - 1]?.current;
                if (thumb) {
                  const thumbCtx = thumb.getContext("2d");
                  thumbCtx.clearRect(0, 0, thumb.width, thumb.height);
                  thumb.width = 0;
                  thumb.height = 0;
                  thumb.dataset.rendered = "";
                }

                mountedPages.delete(pageNum);
                renderedPagesRef.current.delete(pageNum);
              }
            }
          });
        },
        {
          root: pagesContainerRef.current,
          rootMargin: "200px", // Start rendering 200px before page is visible
          threshold: 0.1
        }
      );

      // Observe all page containers
      pageRefs.current.forEach((ref, index) => {
        if (ref?.current) {
          const pageElement = ref.current;
          pageElement.dataset.pageNum = (index + 1).toString();
          if (observerRef.current) {
            observerRef.current.observe(pageElement);
          }
        }
      });

      // Render first page immediately for better UX
      renderSinglePage(1);
      renderedPagesRef.current.add(1);
    };

    // Choose rendering strategy
    if (useLazyRendering) {
      // Wait for DOM to be ready, then setup lazy rendering
      setTimeout(() => {
        if (!cancelled) {
          setupLazyRendering();
        }
      }, 100);
    } else {
      // Use synchronous rendering for low complexity
      renderAllPages();
    }

    return () => {
      cancelled = true;
      // Cancel any ongoing page renders
      renderingPagesRef.current.forEach((task) => {
        if (task && task.cancel) {
          task.cancel();
        }
      });
      renderingPagesRef.current.clear();
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      isRenderedRef.current = false;
      renderedPagesRef.current.clear();
      mountedPagesRef.current.clear();
    };
  }, [pdfDoc, totalPages, isVisible, scale, rotation, isSidebarOpen, isSlidesMode, useLazyRendering, renderSinglePage]);

  // Lazy load thumbnails when they come into view in sidebar
  useEffect(() => {
    if (!pdfDoc || !totalPages || !isSidebarOpen || !isVisible || isSlidesMode) {
      // Clean up observer when sidebar closes
      if (thumbnailObserverRef.current) {
        thumbnailObserverRef.current.disconnect();
        thumbnailObserverRef.current = null;
      }
      return;
    }

    // Wait for thumbnail container to be ready
    const setupThumbnailLazyLoading = () => {
      const thumbnailsContainer = thumbsContainerRef.current;
      if (!thumbnailsContainer) {
        setTimeout(setupThumbnailLazyLoading, 100);
        return;
      }

      // Clean up previous observer
      if (thumbnailObserverRef.current) {
        thumbnailObserverRef.current.disconnect();
      }

      // Reset rendered thumbnails tracking
      renderedThumbnailsRef.current.clear();
      // Cancel any ongoing thumbnail renders
      renderingThumbnailsRef.current.forEach((task) => {
        if (task && task.cancel) {
          task.cancel();
        }
      });
      renderingThumbnailsRef.current.clear();

      // Render thumbnail function
      const renderThumbnail = async (pageNum) => {
        // Skip if already rendered or currently rendering
        if (renderedThumbnailsRef.current.has(pageNum)) return;
        if (renderingThumbnailsRef.current.has(pageNum)) return;

        try {
          const page = await pdfDoc.getPage(pageNum);
          const thumb = thumbRefs.current[pageNum - 1]?.current;

          if (!thumb || thumb.dataset.rendered === "true") return;

          const thumbScale = 0.2;
          const thumbViewport = page.getViewport({ scale: thumbScale, rotation });
          thumb.height = thumbViewport.height;
          thumb.width = thumbViewport.width;

          // Mark as rendering and store the render task
          renderingThumbnailsRef.current.set(pageNum, null);

          const renderTask = page.render({
            canvasContext: thumb.getContext("2d"),
            viewport: thumbViewport,
          });

          // Store the task so we can cancel it if needed
          renderingThumbnailsRef.current.set(pageNum, renderTask);

          await renderTask.promise;

          thumb.dataset.rendered = "true";
          renderedThumbnailsRef.current.add(pageNum);
          renderingThumbnailsRef.current.delete(pageNum);
        } catch (error) {
          // Only log if it's not a cancellation error
          if (error.name !== "RenderingCancelledException") {
            console.error(`Error rendering thumbnail for page ${pageNum}:`, error);
          }
          // Clean up on error
          renderingThumbnailsRef.current.delete(pageNum);
        }
      };

      // Create IntersectionObserver for thumbnails
      thumbnailObserverRef.current = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const thumbElement = entry.target;
              const pageNum = parseInt(thumbElement.dataset.pageNum);
              if (pageNum && !renderedThumbnailsRef.current.has(pageNum)) {
                renderThumbnail(pageNum);
              }
            }
          });
        },
        {
          root: thumbnailsContainer,
          rootMargin: "100px", // Start loading 100px before visible
          threshold: 0.1
        }
      );

      // Observe all thumbnail canvases
      thumbRefs.current.forEach((ref, index) => {
        if (ref?.current) {
          const thumbElement = ref.current.closest('.pdf-thumb') || ref.current.parentElement;
          if (thumbElement) {
            thumbElement.dataset.pageNum = (index + 1).toString();
            thumbnailObserverRef.current.observe(thumbElement);
          }
        }
      });

      // Render first few thumbnails immediately (current page ± 2)
      const currentPageNum = currentPageRef.current || 1;
      const immediateRange = 3; // Render current page ± 3 pages
      for (let i = Math.max(1, currentPageNum - immediateRange);
        i <= Math.min(totalPages, currentPageNum + immediateRange);
        i++) {
        renderThumbnail(i);
      }
    };

    // Setup after a short delay to ensure DOM is ready
    const timeoutId = setTimeout(setupThumbnailLazyLoading, 200);

    return () => {
      clearTimeout(timeoutId);
      // Cancel any ongoing thumbnail renders
      renderingThumbnailsRef.current.forEach((task) => {
        if (task && task.cancel) {
          task.cancel();
        }
      });
      renderingThumbnailsRef.current.clear();
      if (thumbnailObserverRef.current) {
        thumbnailObserverRef.current.disconnect();
        thumbnailObserverRef.current = null;
      }
    };
  }, [pdfDoc, totalPages, isSidebarOpen, isVisible, isSlidesMode, rotation, currentPage]);

  // Track which page is in view using scroll position to avoid flicker
  useEffect(() => {
    if (!isVisible || totalPages === 0 || isSlidesMode) return;
    const container = pagesContainerRef.current;
    if (!container) return;

    let ticking = false;
    let scrollHandler = null;
    let checkTimeout = null;

    const updateCurrentPage = () => {
      // Skip tracking while we are restoring a preserved page
      if (restoringPageRef.current) return;

      if (!container) return;
      const containerRect = container.getBoundingClientRect();
      let bestPage = 1;
      let bestDistance = Number.POSITIVE_INFINITY;

      pageRefs.current.forEach((ref, idx) => {
        const el = ref?.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const elCenter = rect.top + rect.height / 2;
        const containerCenter = containerRect.top + containerRect.height / 2;
        const distance = Math.abs(elCenter - containerCenter);
        if (distance < bestDistance) {
          bestDistance = distance;
          bestPage = idx + 1;
        }
      });

      if (bestPage !== currentPageRef.current) {
        setCurrentPage(bestPage);
      }
    };

    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        updateCurrentPage();
        ticking = false;
      });
    };

    // Wait for pages to be rendered before tracking
    const checkPagesReady = () => {
      const firstPage = pageRefs.current[0]?.current;
      const firstCanvas = canvasRefs.current[0]?.current;

      if (!firstPage || !firstCanvas) {
        // Pages not ready yet, check again
        checkTimeout = setTimeout(checkPagesReady, 100);
        return;
      }

      // Pages are ready, set up tracking
      // Check if we have a preserved page from exiting slides mode
      const preservedPage = preservedPageRef.current;
      const targetPage = preservedPage && preservedPage >= 1 && preservedPage <= totalPages
        ? preservedPage
        : 1;

      // Pause tracking while we restore target
      restoringPageRef.current = true;
      setCurrentPage(targetPage);

      // Scroll to the target page or top
      if (preservedPage && preservedPage >= 1 && preservedPage <= totalPages) {
        // Scroll to preserved page after a delay to ensure DOM is ready
        setTimeout(() => {
          const targetPageEl = pageRefs.current[preservedPage - 1]?.current;
          if (targetPageEl) {
            restoringPageRef.current = true;
            // Align to top to keep page index consistent
            targetPageEl.scrollIntoView({ behavior: "auto", block: "start" });
            setCurrentPage(preservedPage);
            // Allow tracking after scroll settles
            setTimeout(() => {
              restoringPageRef.current = false;
              preservedPageRef.current = null;
            }, 300);
          } else {
            container.scrollTop = 0;
            restoringPageRef.current = false;
            preservedPageRef.current = null;
          }
        }, 300);
      } else {
        // No preserved page, scroll to top
        container.scrollTop = 0;
        setTimeout(() => {
          restoringPageRef.current = false;
        }, 250);
      }

      // Delay initial update to ensure layout is stable
      setTimeout(() => {
        updateCurrentPage();
        scrollHandler = handleScroll;
        container.addEventListener("scroll", scrollHandler, { passive: true });
      }, 400);
    };

    checkPagesReady();

    return () => {
      if (checkTimeout) clearTimeout(checkTimeout);
      if (scrollHandler) {
        container.removeEventListener("scroll", scrollHandler);
      }
    };
  }, [isVisible, totalPages, pdfDoc, isSlidesMode, renderKey]);

  // Capture mouse events in comment mode
  useEffect(() => {
    if (!isCommentMode) return;
    const container = pagesContainerRef.current;
    if (!container) return;

    container.addEventListener("mousedown", handleMouseDown);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      container.removeEventListener("mousedown", handleMouseDown);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isCommentMode, handleMouseDown, handleMouseUp]);

  // Keep thumbnails scrolled to the active page
  useEffect(() => {
    const list = thumbsContainerRef.current;
    if (!list) return;
    const activeItem = list.children?.[currentPage - 1];
    if (activeItem) {
      activeItem.scrollIntoView({ block: "nearest", inline: "nearest" });
    }
  }, [currentPage]);

  // Keep ref in sync with state to avoid effect dependency churn
  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);

  // Force re-render when exiting slides mode
  useEffect(() => {
    // Check if we're exiting slides mode (was true, now false)
    const isExitingSlidesMode = prevSlidesModeRef.current && !isSlidesMode;

    if (isExitingSlidesMode && pdfDoc && totalPages > 0 && isVisible) {
      // Preserve the current page when exiting slides mode
      preservedPageRef.current = currentPage;
      restoringPageRef.current = true;

      // Increment render key to force remount of canvas elements
      setRenderKey((prev) => prev + 1);
    } else if (isSlidesMode) {
      // Clear preserved page when entering slides mode
      preservedPageRef.current = null;
    }

    // Update the ref for next render
    prevSlidesModeRef.current = isSlidesMode;
  }, [isSlidesMode, pdfDoc, totalPages, isVisible, currentPage]);

  // Clean up cursor styles when exiting edit mode
  useEffect(() => {
    if (!isEditMode) {
      // Run cleanup immediately and after a delay to catch async updates
      cleanupDrawMode();
      const timeoutId = setTimeout(cleanupDrawMode, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [isEditMode, cleanupDrawMode]);

  // Calculate fit scale and keep it updated
  useEffect(() => {
    if (!pdfDoc || !containerRef.current) return;

    let mounted = true;
    const calculateFitScale = async () => {
      try {
        const page = await pdfDoc.getPage(1);
        if (!mounted) return;
        const unscaledViewport = page.getViewport({ scale: 1, rotation });
        const available = containerRef.current.clientWidth - 40; // padding
        if (unscaledViewport.width > 0) {
          const newFitScale = available / unscaledViewport.width;
          setFitScale(newFitScale);
        }
      } catch (error) {
        console.error("Error calculating fit scale:", error);
      }
    };

    calculateFitScale();

    const resizeObserver = new ResizeObserver(() => {
      calculateFitScale();
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      mounted = false;
      resizeObserver.disconnect();
    };
  }, [pdfDoc, rotation, isSidebarOpen, isCommentsSidebarOpen, sidebarWidth]);

  const zoomIn = () => setScale((s) => Math.min(s + 0.2, 4));
  const zoomOut = () => setScale((s) => Math.max(s - 0.2, 0.4));
  const resetZoom = () => setScale(1.0);
  const fitWidth = () => {
    // Use the calculated fitScale but clamp it
    setScale(Math.max(0.4, Math.min(4, fitScale)));
  };

  const rotateLeft = () => setRotation((r) => (r + 270) % 360);
  const rotateRight = () => setRotation((r) => (r + 90) % 360);

  const toggleSidebar = () => setIsSidebarOpen((o) => !o);

  const downloadPdf = async () => {
    if (isEditMode) return; // Disable download when draw mode is active
    if (!pdfDoc) return;
    try {
      const data = await pdfDoc.getData();
      const blob = new Blob([data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName || "document.pdf";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download failed:", err);
      setError("Download failed");
    }
  };

  const printPdf = async () => {
    if (isEditMode) return; // Disable print when draw mode is active
    if (!pdfDoc || totalPages === 0) return;

    // Show loading overlay immediately
    const loadingOverlay = document.createElement("div");
    loadingOverlay.className = "pdf-print-loading";
    loadingOverlay.innerHTML = `
      <div class="pdf-print-loading-content">
        <div class="pdf-print-spinner"></div>
        <p>Preparing pages for printing...</p>
      </div>
    `;
    document.body.appendChild(loadingOverlay);

    try {
      // Remove any existing print zone
      const existingZone = document.querySelector(".pdf-print-zone");
      if (existingZone) {
        existingZone.remove();
      }

      // Create print zone container
      const printZone = document.createElement("div");
      printZone.className = "pdf-print-zone";
      document.body.appendChild(printZone);

      // Create @page style rule with margin only (let browser handle page size)
      // This avoids browser prompting for page type selection
      const pageStyle = document.createElement("style");
      pageStyle.id = "pdf-print-page-style";
      pageStyle.textContent = `
        @page {
          margin: 0;
        }
      `;
      // Remove existing style if any
      const existingStyle = document.getElementById("pdf-print-page-style");
      if (existingStyle) {
        existingStyle.remove();
      }
      document.head.appendChild(pageStyle);

      let renderedPages = [];

      // Check if all pages are already rendered and can be reused
      const checkIfRendered = () => {
        if (!isRenderedRef.current || canvasRefs.current.length !== totalPages) {
          return false;
        }
        // Verify all canvases exist and have content
        for (let i = 0; i < totalPages; i++) {
          const canvas = canvasRefs.current[i]?.current;
          if (!canvas || canvas.width === 0 || canvas.height === 0) {
            return false;
          }
        }
        return true;
      };

      if (checkIfRendered()) {
        // Use existing rendered canvases - much faster!
        // Get page dimensions once for all pages
        const firstPage = await pdfDoc.getPage(1);
        const firstViewport = firstPage.getViewport({ scale: 1, rotation });

        for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
          try {
            const existingCanvas = canvasRefs.current[pageNum - 1]?.current;
            if (!existingCanvas || existingCanvas.width === 0 || existingCanvas.height === 0) {
              continue;
            }

            const page = await pdfDoc.getPage(pageNum);
            const viewport = page.getViewport({ scale: 1, rotation });
            const pageWidth = viewport.width;
            const pageHeight = viewport.height;

            // Directly use existing canvas data - it's already rendered
            // Convert to data URL immediately (no re-rendering needed)
            const dataUrl = existingCanvas.toDataURL("image/png", 1.0);

            if (!dataUrl || dataUrl === "data:,") {
              console.error(`Failed to create data URL for page ${pageNum} from existing canvas`);
              continue;
            }

            renderedPages.push({
              pageNum,
              dataUrl,
              pageWidth,
              pageHeight
            });
          } catch (pageErr) {
            console.error(`Error processing existing canvas for page ${pageNum}:`, pageErr);
          }
        }
      }

      // Fallback: Render at print quality if pages not rendered yet
      if (renderedPages.length === 0) {
        const printUnit = 150 / 72;

        const renderPage = async (pageNum) => {
          try {
            const page = await pdfDoc.getPage(pageNum);
            const viewport = page.getViewport({ scale: 1, rotation });

            // Calculate print dimensions in points (PDF units)
            const pageWidth = viewport.width;
            const pageHeight = viewport.height;

            // Create a canvas for this page
            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");

            // Set canvas size for print resolution (150 DPI)
            const canvasWidth = Math.floor(pageWidth * printUnit);
            const canvasHeight = Math.floor(pageHeight * printUnit);
            canvas.width = canvasWidth;
            canvas.height = canvasHeight;

            // Fill white background
            ctx.fillStyle = "rgb(255, 255, 255)";
            ctx.fillRect(0, 0, canvasWidth, canvasHeight);

            // Render page to canvas with print intent
            await page.render({
              canvasContext: ctx,
              intent: "print",
              transform: [printUnit, 0, 0, printUnit, 0, 0],
              viewport: viewport,
            }).promise;

            // Convert canvas to data URL
            const dataUrl = canvas.toDataURL("image/png", 1.0);

            // Clean up canvas
            canvas.width = 0;
            canvas.height = 0;

            if (!dataUrl || dataUrl === "data:,") {
              console.error(`Failed to create data URL for page ${pageNum}`);
              return null;
            }

            return {
              pageNum,
              dataUrl,
              pageWidth,
              pageHeight
            };
          } catch (pageErr) {
            console.error(`Error rendering page ${pageNum} for print:`, pageErr);
            return null;
          }
        };

        // Render all pages in parallel
        const pagePromises = Array.from({ length: totalPages }, (_, i) =>
          renderPage(i + 1)
        );
        const renderedPagesResults = await Promise.all(pagePromises);
        renderedPages = renderedPagesResults
          .filter(p => p !== null)
          .sort((a, b) => a.pageNum - b.pageNum);
      }

      if (renderedPages.length === 0) {
        throw new Error("No pages were successfully rendered for printing");
      }

      // Create page containers and images
      // Data URLs are immediately available, so we can create images synchronously
      renderedPages.forEach(({ pageNum, dataUrl, pageWidth, pageHeight }) => {
        const pageContainer = document.createElement("div");
        pageContainer.className = "pdf-print-page";

        const img = document.createElement("img");
        img.className = "pdf-print-image";
        img.src = dataUrl;
        img.style.width = `${pageWidth}pt`;
        img.style.display = "block";

        pageContainer.appendChild(img);
        printZone.appendChild(pageContainer);
      });

      // Small delay to ensure DOM is updated (data URLs load synchronously)
      await new Promise(resolve => requestAnimationFrame(resolve));

      // Remove loading overlay
      if (loadingOverlay && loadingOverlay.parentNode) {
        loadingOverlay.remove();
      }

      // Add classes to body/html for print styling
      document.documentElement.classList.add("pdf-printing");
      document.body.classList.add("pdf-printing");

      // Trigger print dialog
      window.print();

      // Cleanup function
      const cleanup = () => {
        document.documentElement.classList.remove("pdf-printing");
        document.body.classList.remove("pdf-printing");

        // Remove print zone and style after a short delay to ensure print completes
        setTimeout(() => {
          if (printZone && printZone.parentNode) {
            printZone.remove();
          }
          const styleElement = document.getElementById("pdf-print-page-style");
          if (styleElement) {
            styleElement.remove();
          }
        }, 500);
      };

      // Handle print dialog close
      window.addEventListener("afterprint", cleanup, { once: true });

      // Fallback cleanup for browsers that don't fire afterprint reliably
      // Use mousemove event as a fallback (user moves mouse after closing print dialog)
      let cleanupTimeout;
      const mouseMoveHandler = () => {
        clearTimeout(cleanupTimeout);
        cleanupTimeout = setTimeout(() => {
          cleanup();
          document.removeEventListener("mousemove", mouseMoveHandler);
        }, 1000);
      };
      document.addEventListener("mousemove", mouseMoveHandler);

    } catch (err) {
      console.error("Print failed:", err);
      setError("Print failed");

      // Remove loading overlay on error
      if (loadingOverlay && loadingOverlay.parentNode) {
        loadingOverlay.remove();
      }

      // Cleanup on error
      const printZone = document.querySelector(".pdf-print-zone");
      if (printZone) {
        printZone.remove();
      }
      const styleElement = document.getElementById("pdf-print-page-style");
      if (styleElement) {
        styleElement.remove();
      }
    }
  };

  const enterFullscreen = () => {
    if (isEditMode) return; // Disable fullscreen when draw mode is active
    const el = containerRef.current;
    if (!el) return;
    if (document.fullscreenElement) {
      document.exitFullscreen?.();
    } else {
      el.requestFullscreen?.();
    }
  };

  const handleSaveComment = useCallback(async () => {
    if (!pendingComment || !commentText.trim()) return;

    try {
      const newComment = await saveCommentToBackend(pendingComment, commentText);
      if (newComment) {
        setComments((prev) => [...prev, newComment]);
        setPendingComment(null);
        setCommentText("");
      }
    } catch (error) {
      console.error("Failed to create a comment", error);
    }
  }, [pendingComment, commentText, saveCommentToBackend]);

  const handleCancelComment = useCallback((e) => {
    if (e && e.stopPropagation) {
      e.stopPropagation();
    }
    setPendingComment(null);
    setCommentText("");
    setIsCommentMode(false);
  }, []);

  // Delete comment handler
  const handleDeleteComment = useCallback(async (commentId) => {
    try {
      await deleteCommentFromBackend(commentId);
      // Remove from local state after successful deletion
      setComments((prev) => prev.filter((c) => c.id !== commentId));
      setCommentMenuOpen(null);
    } catch (error) {
      console.error("Failed to delete comment:", error);
    }
  }, [deleteCommentFromBackend]);

  // Resolve comment handler - uses updateComment with state parameter
  const handleResolveComment = useCallback(async (commentId) => {
    try {
      // Update comment state to "resolved" without changing the text
      await updateCommentInBackend(commentId, null, "resolved");
    } catch (error) {
      console.error("Failed to resolve comment:", error);
    }
  }, [updateCommentInBackend]);

  // Reopen comment handler - uses updateComment with state parameter
  const handleReopenComment = useCallback(async (commentId) => {
    try {
      // Update comment state to "active" without changing the text
      await updateCommentInBackend(commentId, null, "active");
    } catch (error) {
      console.error("Failed to reopen comment:", error);
    }
  }, [updateCommentInBackend]);

  // Edit comment handler
  const handleStartEdit = useCallback((commentId, currentText) => {
    setEditingCommentId(commentId);
    setEditCommentText(currentText);
    setCommentMenuOpen(null);
  }, []);

  const handleSaveEdit = useCallback(async (commentId) => {
    if (!editCommentText.trim()) return;

    try {
      await updateCommentInBackend(commentId, editCommentText);
      setEditingCommentId(null);
      setEditCommentText("");
    } catch (error) {
      console.error("Failed to edit comment:", error);
      // Close edit box on error
      setEditingCommentId(null);
      setEditCommentText("");
    }
  }, [editCommentText, updateCommentInBackend]);

  const handleCancelEdit = useCallback(() => {
    setEditingCommentId(null);
    setEditCommentText("");
  }, []);

  // Reply handlers
  const handleStartReply = useCallback((commentId) => {
    setReplyingToCommentId(commentId);
    setReplyText("");
  }, []);

  const handleSaveReply = useCallback(async (commentId) => {
    if (!replyText.trim()) return;

    try {
      await addReplyToBackend(commentId, replyText);
      // Comments will be reloaded in addReply function
    } catch (error) {
      console.error("Failed to add reply:", error);
    }
  }, [replyText, addReplyToBackend]);

  const handleCancelReply = useCallback(() => {
    setReplyingToCommentId(null);
    setReplyText("");
  }, []);

  // Edit reply handlers
  const handleStartEditReply = useCallback((commentId, replyIndex, currentText) => {
    setEditingReply({ commentId, replyIndex });
    setEditReplyText(currentText);
    setReplyMenuOpen(null);
  }, []);

  const handleSaveEditReply = useCallback(async (commentId, replyIndex) => {
    if (!editReplyText.trim()) return;

    try {
      await updateReplyInBackend(commentId, replyIndex, editReplyText);
      setEditingReply(null);
      setEditReplyText("");
      // Local state is updated in updateReply function in useComment.jsx
    } catch (error) {
      console.error("Failed to edit reply:", error);
      // Close edit box on error
      setEditingReply(null);
      setEditReplyText("");
    }
  }, [editReplyText, updateReplyInBackend]);

  const handleCancelEditReply = useCallback(() => {
    setEditingReply(null);
    setEditReplyText("");
  }, []);

  // Delete reply handler
  const handleDeleteReply = useCallback(async (commentId, replyIndex) => {
    try {
      await deleteReplyFromBackend(commentId, replyIndex);
      // Update local state
      setComments((prev) =>
        prev.map((c) => {
          if (c.id === commentId && c.replies) {
            const newReplies = [...c.replies];
            newReplies.splice(replyIndex, 1);
            return { ...c, replies: newReplies };
          }
          return c;
        })
      );
      setReplyMenuOpen(null);
    } catch (error) {
      console.error("Failed to delete reply:", error);
    }
  }, [deleteReplyFromBackend, setComments]);

  // Close menu, reply input, edit input, or add comment panel when clicking anywhere
  useEffect(() => {
    const handleClickAnywhere = (event) => {
      // Handle comment menu close (Edit/Delete options)
      if (commentMenuOpen !== null) {
        const menuButton = event.target.closest(".pdf-comments-sidebar-menu-button");
        const menuDropdown = event.target.closest(".pdf-comments-sidebar-menu-dropdown");

        if (!menuButton && !menuDropdown) {
          setCommentMenuOpen(null);
        }
      }

      // Handle reply menu close (Edit/Delete options)
      if (replyMenuOpen !== null) {
        const replyMenuButton = event.target.closest(".pdf-comments-sidebar-reply-menu-button");
        const replyMenuDropdown = event.target.closest(".pdf-comments-sidebar-reply-menu-dropdown");

        if (!replyMenuButton && !replyMenuDropdown) {
          setReplyMenuOpen(null);
        }
      }

      // Handle reply input close
      if (replyingToCommentId !== null && replyText.trim() === "") {
        const replyInput = event.target.closest(".pdf-comments-sidebar-reply-input");
        const replyButton = event.target.closest(".pdf-comments-sidebar-reply-button");

        if (!replyInput && !replyButton) {
          handleCancelReply();
        }
      }

      // Handle edit input close
      if (editingCommentId !== null && editCommentText.trim() === "") {
        const editInput = event.target.closest(".pdf-comments-sidebar-edit-input");
        if (!editInput) {
          handleCancelEdit();
        }
      }

      // Handle edit reply input close
      if (editingReply !== null && editReplyText.trim() === "") {
        const editReplyInput = event.target.closest(".pdf-comments-sidebar-edit-reply-input");
        if (!editReplyInput) {
          handleCancelEditReply();
        }
      }

      // Handle add comment panel close
      if (pendingComment !== null && commentText.trim() === "") {
        const addCommentPanel = event.target.closest(".pdf-comment-sidebar-panel");
        const commentToolbarButton = event.target.closest(".pdf-toolbar-button");
        const canvas = event.target.closest(".pdf-canvas");

        if (!addCommentPanel && !commentToolbarButton && !canvas) {
          handleCancelComment();
        }
      }

      // Handle mention dropdown close
      if (mentionState.active) {
        const mentionDropdown = event.target.closest(".pdf-mention-dropdown");
        if (!mentionDropdown) {
          setMentionState(prev => ({ ...prev, active: false }));
        }
      }

      // Handle filter menu close
      if (filterMenuOpen) {
        const filterButton = event.target.closest(".pdf-comments-sidebar-filter-button");
        const filterDropdown = event.target.closest(".pdf-comments-sidebar-filter-dropdown");

        if (!filterButton && !filterDropdown) {
          setFilterMenuOpen(false);
        }
      }
    };

    if (commentMenuOpen !== null || replyMenuOpen !== null || replyingToCommentId !== null || editingCommentId !== null || editingReply !== null || pendingComment !== null || mentionState.active || filterMenuOpen) {
      // Use capture phase to catch all clicks
      document.addEventListener("mousedown", handleClickAnywhere, true);
      return () => {
        document.removeEventListener("mousedown", handleClickAnywhere, true);
      };
    }
  }, [
    commentMenuOpen,
    replyMenuOpen,
    replyingToCommentId,
    editingCommentId,
    editingReply,
    pendingComment,
    mentionState.active,
    replyText,
    editCommentText,
    editReplyText,
    commentText,
    handleCancelReply,
    handleCancelEdit,
    handleCancelEditReply,
    handleCancelComment,
    filterMenuOpen
  ]);

  const toggleSlidesMode = () => {
    if (isEditMode) return; // Disable slides when draw mode is active
    setIsCommentMode(false);
    setIsSlidesMode((prev) => !prev);
  };

  const handleSlidesPageChange = (pageNum) => {
    setCurrentPage(pageNum);
  };


  // Drawing/Edit mode handlers
  const handleToggleEditMode = useCallback(async () => {
    // If closing edit mode, check for unsaved changes
    if (isEditMode) {
      if (hasUnsavedChanges) {
        // Show confirmation dialog instead of closing immediately
        setCloseAction("draw");
        setShowCloseConfirm(true);
        return; // Keep edit mode open until user confirms
      } else {
        // No unsaved changes, close immediately
        cleanupDrawMode();
        setIsEditMode(false);
        setIsPreparingEditMode(false);
        return;
      }
    }

    // Enabling edit mode - ensure current page is rendered first
    if (!pdfDoc || totalPages === 0) {
      showToast("PDF not loaded. Please wait.", "error");
      return;
    }

    const currentPageNum = currentPageRef.current || currentPage;

    // Check if we need to render pages before enabling edit mode
    if (useLazyRendering) {
      // Check if current page is already rendered
      if (!renderedPagesRef.current.has(currentPageNum)) {
        setIsPreparingEditMode(true);
        try {
          // Render current page first
          const success = await renderSinglePage(currentPageNum);
          if (!success) {
            showToast("Failed to prepare edit mode. Please try again.", "error");
            setIsPreparingEditMode(false);
            return;
          }
        } catch (err) {
          console.error("Error preparing edit mode:", err);
          showToast("Failed to prepare edit mode. Please try again.", "error");
          setIsPreparingEditMode(false);
          return;
        }
        setIsPreparingEditMode(false);
      }

      // Pre-render adjacent pages in background (non-blocking)
      const buffer = 2;
      const pagesToRender = [];
      for (let i = Math.max(1, currentPageNum - buffer);
        i <= Math.min(totalPages, currentPageNum + buffer);
        i++) {
        if (i !== currentPageNum && !renderedPagesRef.current.has(i)) {
          pagesToRender.push(i);
        }
      }

      // Render adjacent pages in background
      pagesToRender.forEach((pageNum, index) => {
        setTimeout(() => {
          renderSinglePage(pageNum).catch(err => {
            console.warn(`Failed to pre-render page ${pageNum} for edit mode:`, err);
          });
        }, index * 50);
      });
    }

    // Disable comment mode and slides mode when entering edit mode
    setIsCommentMode(false);
    setIsSlidesMode(false);
    // Close comments sidebar when entering edit mode
    setIsCommentsSidebarOpen(false);
    setPendingComment(null);
    setCommentText("");

    // Enable edit mode
    setIsEditMode(true);
  }, [isEditMode, cleanupDrawMode, hasUnsavedChanges, pdfDoc, totalPages, currentPage, useLazyRendering, renderSinglePage, showToast]);


  const handleDrawToolChange = useCallback((tool) => {
    setSelectedDrawTool(tool);
  }, []);

  const handleDrawColorChange = useCallback((color) => {
    setSelectedDrawColor(color);
  }, []);

  const handleBrushSizeChange = useCallback((size) => {
    setBrushSize(size);
  }, []);

  // Undo/Redo handlers
  const handleUndo = useCallback(() => {
    if (drawCanvasRef.current?.undo) {
      drawCanvasRef.current.undo();
    }
  }, []);

  const handleRedo = useCallback(() => {
    if (drawCanvasRef.current?.redo) {
      drawCanvasRef.current.redo();
    }
  }, []);

  // Get undo/redo availability
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  // Update undo/redo availability
  useEffect(() => {
    if (isEditMode && drawCanvasRef.current) {
      const updateAvailability = () => {
        if (drawCanvasRef.current?.canUndo) {
          setCanUndo(drawCanvasRef.current.canUndo());
        } else {
          setCanUndo(false);
        }
        if (drawCanvasRef.current?.canRedo) {
          setCanRedo(drawCanvasRef.current.canRedo());
        } else {
          setCanRedo(false);
        }
      };

      // Update immediately and on interval to catch state changes
      updateAvailability();
      const interval = setInterval(updateAvailability, 50); // More frequent polling

      return () => clearInterval(interval);
    } else {
      setCanUndo(false);
      setCanRedo(false);
    }
  }, [isEditMode, hasUnsavedChanges]);

  const handleDrawSave = useCallback(async () => {
    if (!pdfDoc || !fo || !fileName || totalPages === 0) {
      showToast("Cannot save: PDF not loaded", "error");
      return;
    }

    setIsSaving(true);
    setSaveProgress(0);
    saveAbortRef.current = false;
    try {
      // Step 1: OPTIMIZED - Only check pages that have drawing canvases created
      // Instead of looping through all 944 pages, check only pages with canvases
      const pagesWithDrawings = [];
      const container = document.querySelector(".pdf-pages");

      if (container) {
        // Get all drawing canvases that exist (much faster than checking all pages)
        const drawCanvases = container.querySelectorAll(".pdf-draw-canvas");
        drawCanvases.forEach((canvas) => {
          if (canvas.width > 0 && canvas.height > 0) {
            // Find which page this canvas belongs to
            const pageElement = canvas.closest(".pdf-page");
            if (pageElement) {
              const pageIndex = pageRefs.current.findIndex(ref => ref?.current === pageElement);
              if (pageIndex !== -1) {
                const pageNum = pageIndex + 1;
                // Double-check this page actually has drawings
                const drawCanvas = pageElement.querySelector(".pdf-draw-canvas");
                if (drawCanvas && drawCanvas.width > 0 && drawCanvas.height > 0) {
                  pagesWithDrawings.push(pageNum);
                }
              }
            }
          }
        });
      } else {
        // Fallback: if container not found, check pages that have drawing canvases via pageRefs
        // This is still better than checking all pages
        pageRefs.current.forEach((pageRef, index) => {
          if (!pageRef?.current) return;
          const drawCanvas = pageRef.current.querySelector(".pdf-draw-canvas");
          if (drawCanvas && drawCanvas.width > 0 && drawCanvas.height > 0) {
            pagesWithDrawings.push(index + 1);
          }
        });
      }

      if (pagesWithDrawings.length === 0) {
        showToast("No drawings to save", "info");
        setIsSaving(false);
        setSaveProgress(0);
        return;
      }

      setSaveProgress(5); // 5% - identified pages

      // Check for abort after initial setup
      if (saveAbortRef.current) throw new Error("__ABORT__");

      // Step 2: Get unrotated page dimensions only for pages with drawings
      const unrotatedViewports = [];
      for (let i = 0; i < pagesWithDrawings.length; i++) {
        const pageNum = pagesWithDrawings[i];
        try {
          const page = await pdfDoc.getPage(pageNum);
          const unrotatedViewport = page.getViewport({ scale: 1, rotation: 0 });
          unrotatedViewports[pageNum - 1] = unrotatedViewport;
        } catch (err) {
          console.error(`Error getting viewport for page ${pageNum}:`, err);
          unrotatedViewports[pageNum - 1] = null;
        }
        // Update progress
        setSaveProgress(Math.round(5 + (i + 1) / pagesWithDrawings.length * 15)); // 5-20%
        // Yield to UI every few pages
        if (i % 3 === 0) {
          await new Promise(resolve => setTimeout(resolve, 0));
        }
        if (saveAbortRef.current) throw new Error("__ABORT__");
      }

      const pageDrawings = []; // Array of { pageIndex, imageDataUrl } for pages with drawings

      // Step 3: Process only pages with drawings (batch processing with UI yields)
      for (let idx = 0; idx < pagesWithDrawings.length; idx++) {
        const i = pagesWithDrawings[idx];
        const pageRef = pageRefs.current[i - 1]?.current;
        if (!pageRef) {
          continue;
        }

        const pdfCanvas = pageRef.querySelector(".pdf-canvas");
        const drawCanvas = pageRef.querySelector(".pdf-draw-canvas");

        if (!pdfCanvas) {
          continue;
        }

        // Check if there are any drawings on this page
        const hasDrawings = drawCanvas && drawCanvas.width > 0 && drawCanvas.height > 0;

        if (!hasDrawings) {
          // No drawings on this page, skip it (original PDF content will remain unchanged)
          pageDrawings.push(null);
          continue;
        }

        // Get unrotated viewport for this page
        const unrotatedViewport = unrotatedViewports[i - 1];
        if (!unrotatedViewport) {
          console.warn(`Could not get unrotated viewport for page ${i}, skipping`);
          pageDrawings.push(null);
          continue;
        }

        // Create final canvas with unrotated dimensions
        // Apply quality setting to reduce file size if needed
        const drawingsCanvas = document.createElement("canvas");
        const unrotatedWidth = unrotatedViewport.width;
        const unrotatedHeight = unrotatedViewport.height;
        drawingsCanvas.width = Math.round(unrotatedWidth * DRAWING_EXPORT_QUALITY);
        drawingsCanvas.height = Math.round(unrotatedHeight * DRAWING_EXPORT_QUALITY);
        const drawingsCtx = drawingsCanvas.getContext("2d", { willReadFrequently: true });

        // Start with transparent background
        drawingsCtx.clearRect(0, 0, drawingsCanvas.width, drawingsCanvas.height);

        // Transform the rotated drawing canvas to unrotated coordinates
        if (rotation === 0) {
          // No rotation, just scale and draw
          // Account for quality setting in destination dimensions
          const destWidth = drawingsCanvas.width;
          const destHeight = drawingsCanvas.height;
          drawingsCtx.drawImage(drawCanvas, 0, 0, drawCanvas.width, drawCanvas.height, 0, 0, destWidth, destHeight);
        } else {
          // Apply inverse rotation transformation
          drawingsCtx.save();

          const rotatedWidth = drawCanvas.width;
          const rotatedHeight = drawCanvas.height;

          // Center the transformation at the center of the scaled canvas
          const scaledWidth = drawingsCanvas.width;
          const scaledHeight = drawingsCanvas.height;
          drawingsCtx.translate(scaledWidth / 2, scaledHeight / 2);

          // Apply inverse rotation
          if (rotation === 90) {
            // Rotate -90° (counter-clockwise) to undo 90° clockwise rotation
            drawingsCtx.rotate(-Math.PI / 2);
            // For 90° rotation: rotated canvas has swapped dimensions
            // Scale to fit scaled dimensions (note: width/height are swapped after rotation)
            const scaleX = scaledHeight / rotatedWidth;
            const scaleY = scaledWidth / rotatedHeight;
            drawingsCtx.scale(scaleX, scaleY);
            // Draw centered (after rotation, the canvas center is at origin)
            drawingsCtx.drawImage(drawCanvas, -rotatedWidth / 2, -rotatedHeight / 2);
          } else if (rotation === 180) {
            // Rotate 180° to undo 180° rotation
            drawingsCtx.rotate(Math.PI);
            // For 180° rotation: dimensions are the same, just rotated
            const scaleX = scaledWidth / rotatedWidth;
            const scaleY = scaledHeight / rotatedHeight;
            drawingsCtx.scale(scaleX, scaleY);
            drawingsCtx.drawImage(drawCanvas, -rotatedWidth / 2, -rotatedHeight / 2);
          } else if (rotation === 270) {
            // Rotate 90° (counter-clockwise) to undo 270° clockwise rotation
            drawingsCtx.rotate(Math.PI / 2);
            // For 270° rotation: rotated canvas has swapped dimensions
            // Scale to fit scaled dimensions (note: width/height are swapped after rotation)
            const scaleX = scaledHeight / rotatedWidth;
            const scaleY = scaledWidth / rotatedHeight;
            drawingsCtx.scale(scaleX, scaleY);
            // Draw centered (after rotation, the canvas center is at origin)
            drawingsCtx.drawImage(drawCanvas, -rotatedWidth / 2, -rotatedHeight / 2);
          }

          drawingsCtx.restore();
        }

        // Convert to PNG format using Web Worker (offloads main thread)
        // PNG is necessary here because JPEG doesn't support transparency
        // Without transparency, the black background would cover the PDF content
        const imageData = drawingsCtx.getImageData(0, 0, drawingsCanvas.width, drawingsCanvas.height);

        // Use Web Worker for PNG conversion if available, otherwise fallback to synchronous
        let dataUrl;
        if (typeof Worker !== 'undefined' && pngWorkerRef.current) {
          try {
            dataUrl = await new Promise((resolve, reject) => {
              const worker = pngWorkerRef.current;
              let isResolved = false;

              // Store timeout in ref for cleanup
              workerTimeoutRef.current = setTimeout(() => {
                if (!isResolved) {
                  isResolved = true;
                  worker.removeEventListener('message', handleMessage);
                  reject(new Error('PNG conversion timeout'));
                }
              }, 10000); // 10 second timeout

              const handleMessage = (e) => {
                if (isResolved) return;
                isResolved = true;

                if (workerTimeoutRef.current) {
                  clearTimeout(workerTimeoutRef.current);
                  workerTimeoutRef.current = null;
                }
                worker.removeEventListener('message', handleMessage);

                if (e.data.success) {
                  resolve(e.data.dataUrl);
                } else {
                  reject(new Error(e.data.error || 'PNG conversion failed'));
                }
              };

              worker.addEventListener('message', handleMessage);
              worker.postMessage({
                canvasData: imageData.data.buffer,
                width: drawingsCanvas.width,
                height: drawingsCanvas.height
              }, [imageData.data.buffer]);
            });
          } catch (workerError) {
            // Cleanup timeout on error
            if (workerTimeoutRef.current) {
              clearTimeout(workerTimeoutRef.current);
              workerTimeoutRef.current = null;
            }
            console.warn('Web Worker PNG conversion failed, using fallback:', workerError);
            // Fallback to synchronous conversion
            dataUrl = drawingsCanvas.toDataURL("image/png");
          }
        } else {
          // Fallback to synchronous conversion
          dataUrl = drawingsCanvas.toDataURL("image/png");
        }

        pageDrawings.push({ pageIndex: i - 1, imageDataUrl: dataUrl });

        // Update progress (20-60% for processing drawings)
        setSaveProgress(Math.round(20 + ((idx + 1) / pagesWithDrawings.length) * 40));

        // Yield to UI every page to keep it responsive
        await new Promise(resolve => setTimeout(resolve, 0));
        if (saveAbortRef.current) throw new Error("__ABORT__");
      }

      // Use latest bytes (if we already saved once during this session),
      // otherwise fall back to original bytes from storage.
      const basePdfBytes = latestPdfBytesRef.current || (await getOriginalPdfBytes());

      if (!basePdfBytes) {
        throw new Error("Failed to get original PDF data");
      }

      setSaveProgress(60); // 60% - starting PDF processing

      if (saveAbortRef.current) throw new Error("__ABORT__");

      // Use pdf-lib to load the original PDF and embed drawings as images
      const { PDFDocument } = await import("pdf-lib");
      const pdfDocLib = await PDFDocument.load(basePdfBytes);

      // Step 4: Embed drawings on pages that have them (batch with progress)
      for (let idx = 0; idx < pageDrawings.length; idx++) {
        const pageDrawing = pageDrawings[idx];
        const page = pdfDocLib.getPage(pageDrawing.pageIndex);
        const { width, height } = page.getSize();

        // Convert PNG data URL to image (PNG required for transparency)
        // This allows the original PDF content to show through the transparent areas
        const img = await pdfDocLib.embedPng(pageDrawing.imageDataUrl);

        // Scale image to fit page exactly
        const imgDims = img.scale(1);
        const scaleX = width / imgDims.width;
        const scaleY = height / imgDims.height;
        const scale = Math.min(scaleX, scaleY);

        // Draw transparent drawing overlay on top of original PDF content
        // The original PDF vector content remains intact underneath
        page.drawImage(img, {
          x: 0,
          y: 0,
          width: imgDims.width * scale,
          height: imgDims.height * scale,
          opacity: 1.0, // Fully opaque drawings
        });

        // Update progress (60-85% for embedding)
        setSaveProgress(Math.round(60 + ((idx + 1) / pageDrawings.length) * 25));

        if (idx % 2 === 0) {
          await new Promise(resolve => setTimeout(resolve, 0));
        }
        if (saveAbortRef.current) throw new Error("__ABORT__");
      }

      setSaveProgress(85); // 85% - saving PDF
      if (saveAbortRef.current) throw new Error("__ABORT__");
      // Save PDF as bytes
      const pdfBytes = await pdfDocLib.save();
      setSaveProgress(90); // 90% - PDF saved, uploading

      const pdfFile = new File([pdfBytes], fileName, { type: "application/pdf" });

      // Upload using SDK (similar to uploadFile in FilesWrapper)
      await uploadPdfWithDrawings(pdfFile, dupCurrentPath);

      setSaveProgress(100); // 100% - complete
      showToast("PDF with drawings saved successfully", "success");

      // Update local "latest bytes" so subsequent saves include prior saved drawings
      // Make a copy to avoid PDF.js modifying the original array
      latestPdfBytesRef.current = new Uint8Array(pdfBytes);

      // Swap the in-memory PDF.js document to the newly saved bytes.
      // This ensures drawings show correctly in rotation, slides view, download, print, etc.
      try {
        const newPdf = await pdfjsLib.getDocument({ data: pdfBytes }).promise;
        // Best-effort cleanup of prior document to free memory
        try {
          if (pdfDoc?.destroy) {
            pdfDoc.destroy();
          }
        } catch (e) {
          // ignore
        }
        setPdfDoc(newPdf);
        const pages = newPdf.numPages;
        setTotalPages(pages);
        // Rebuild refs (PDF instance changed)
        canvasRefs.current = Array.from({ length: pages }, () => createRef());
        thumbRefs.current = Array.from({ length: pages }, () => createRef());
        pageRefs.current = Array.from({ length: pages }, () => createRef());
        isRenderedRef.current = false;
        setRenderKey((k) => k + 1);

        // Now that PDF content contains the drawings, clear all drawing layers
        if (drawCanvasRef.current?.clearSavedDrawings) {
          drawCanvasRef.current.clearSavedDrawings();
        }
        if (drawCanvasRef.current?.clearDrawings) {
          drawCanvasRef.current.clearDrawings();
        }
      } catch (e) {
        console.error("Failed to reload PDF from saved bytes:", e);
      }

      // Reset unsaved changes flag after successful save
      setHasUnsavedChanges(false);

      // Update undo/redo availability after save (should be disabled now)
      if (drawCanvasRef.current?.canUndo) {
        setCanUndo(drawCanvasRef.current.canUndo());
      } else {
        setCanUndo(false);
      }
      if (drawCanvasRef.current?.canRedo) {
        setCanRedo(drawCanvasRef.current.canRedo());
      } else {
        setCanRedo(false);
      }

      if (onSaveComplete) {
        onSaveComplete();
      }
    } catch (error) {
      if (error.message === "__ABORT__") {
        // Save was aborted by user — silently stop, no toast
        setSaveProgress(0);
      } else {
        // On failure - drawings remain on canvas, user can try again
        showToast(`Failed to save PDF: ${error.message}`, "error");
        setSaveProgress(0);
      }
    } finally {
      setIsSaving(false);
    }
  }, [pdfDoc, fo, fileName, totalPages, pageRefs, dupCurrentPath, showToast, onSaveComplete, rotation]);

  // Helper to get original PDF bytes
  const getOriginalPdfBytes = useCallback(async () => {
    if (!fo) return null;

    try {
      const fileSize = await fo.getSize();
      const chunks = [];
      const chunkSize = 4 * 1024 * 1024; // 4MB

      for (let offset = 0; offset < fileSize; offset += chunkSize) {
        const remainingSize = Math.min(chunkSize, fileSize - offset);
        const path = fo.getParentPath() + "/" + (dupCurrentPath.length === 1 && dupCurrentPath[0] === "shared" ? fo.getFileUuId() : fo.getName());
        const task = new Task(fileSize, fileName, "Downloading");
        const chunk = await fo.getChunk(offset, path, task, remainingSize);
        chunks.push(chunk);
      }

      // Combine chunks
      const totalLength = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
      const combined = new Uint8Array(totalLength);
      let offset = 0;
      for (const chunk of chunks) {
        combined.set(chunk, offset);
        offset += chunk.length;
      }

      return combined;
    } catch (error) {
      console.error("Error getting original PDF bytes:", error);
      return null;
    }
  }, [fo, fileName, dupCurrentPath]);

  // Upload PDF with drawings using SDK
  const uploadPdfWithDrawings = useCallback(async (file, parentPathArray) => {
    if (!fo) {
      throw new Error("File object not available");
    }

    const chunkSize = 4 * 1024 * 1024; // 4MB
    const totalChunks = Math.ceil(file.size / chunkSize);

    const newFo = new IzFile();

    newFo.setSize(file.size);

    // Construct parent path from dupCurrentPath (same logic as comments)
    const parentPath = parentPathArray?.length === 0 ? "/" : `/${parentPathArray.join("/")}`;
    newFo.setParentPath(parentPath);

    // Use the same file name logic as the original file
    let uploadFileName = fileName;
    if (parentPathArray.length === 1 && parentPathArray[0] === "shared") {
      uploadFileName = fo.getFileUuId();
    }
    newFo.setFileName(uploadFileName);

    const task = new Task(file.size, uploadFileName, "Uploading");

    // Upload directly - SDK will automatically handle versioning
    await newFo.startWrite(file.type);

    let uploadedChunks = 0;
    for (let offset = 0; offset < file.size; offset += chunkSize) {
      const chunk = file.slice(offset, offset + chunkSize);
      const arrayBuffer = await chunk.arrayBuffer();
      await newFo.putChunk(new Uint8Array(arrayBuffer), offset, task, null);
      uploadedChunks++;
    }

    await newFo.finalizeWrite("application/pdf", [], file.size);
  }, [fo, fileName]);

  const handleDrawClose = useCallback(() => {
    if (isSaving) {
      // Abort the in-progress save and show confirmation to discard
      saveAbortRef.current = true;
      setCloseAction("draw");
      setShowCloseConfirm(true);
    } else if (hasUnsavedChanges) {
      setCloseAction("draw");
      setShowCloseConfirm(true);
    } else {
      setIsEditMode(false);
      setIsPreparingEditMode(false);
    }
  }, [hasUnsavedChanges, isSaving]);

  // Handle confirmation to close without saving
  const handleConfirmCloseWithoutSave = useCallback(() => {
    // Abort any in-progress save
    saveAbortRef.current = true;
    setIsSaving(false);
    setSaveProgress(0);

    // Clear all unsaved drawings
    if (drawCanvasRef.current?.clearDrawings) {
      drawCanvasRef.current.clearDrawings();
    }
    cleanupDrawMode();
    setShowCloseConfirm(false);
    resetDrawState();
    setIsEditMode(false);
    setIsPreparingEditMode(false);

    // If closing PDF, also close the PDF viewer
    if (closeAction === "pdf") {
      setIsSidebarOpen(false);
      setIsCommentsSidebarOpen(false);
      handleCancel();
    }

    setCloseAction(null);
  }, [closeAction, handleCancel, cleanupDrawMode, resetDrawState]);

  // Handle cancel close confirmation
  const handleCancelCloseConfirm = useCallback(() => {
    setShowCloseConfirm(false);
    setCloseAction(null);
  }, []);

  // Callback to track drawing changes
  // Debounced drawing change handler for better performance
  const handleDrawingsChange = useCallback((hasDrawings) => {
    // Clear existing timeout
    if (drawingsChangeTimeoutRef.current) {
      clearTimeout(drawingsChangeTimeoutRef.current);
    }

    // Use requestAnimationFrame for smoother updates, then debounce state update
    // This prevents blocking during active drawing while still debouncing state changes
    requestAnimationFrame(() => {
      drawingsChangeTimeoutRef.current = setTimeout(() => {
        setHasUnsavedChanges(hasDrawings);
      }, 500); // Increased to 500ms debounce for better performance
    });
  }, []);

  // Handle PDF viewer close with unsaved changes check
  const handlePDFViewerClose = useCallback(() => {
    if (isEditMode && hasUnsavedChanges) {
      setCloseAction("pdf");
      setShowCloseConfirm(true);
    } else {
      setIsSidebarOpen(false);
      setIsCommentsSidebarOpen(false);
      handleCancel();
    }
  }, [isEditMode, hasUnsavedChanges, handleCancel]);

  // Filter comments for current page using hook function
  const currentPageComments = useMemo(() => {
    return getCurrentPageComments(currentPage);
  }, [getCurrentPageComments, currentPage]);

  // Get comments based on filter mode and status
  const displayedComments = useMemo(() => {
    let filtered;
    // First filter by page (pagewise or all)
    if (commentFilter === "all") {
      filtered = comments;
    } else {
      filtered = currentPageComments;
    }
    // Then filter by status based on checkbox selections
    return filtered.filter((c) => {
      const commentState = c.state || "active";
      if (commentState === "resolved") {
        return statusFilter.resolved;
      } else {
        return statusFilter.active;
      }
    });
  }, [commentFilter, statusFilter, comments, currentPageComments]);

  if (!isVisible) return null;

  return (
    <Drawer
      isVisible={isVisible}
      handleCancel={handlePDFViewerClose}
      showFooter={false}
      showHeader={true}
      heading={
        <div className="pdf-drawer-heading">
          <span className="pdf-drawer-filename">{fileName || "PDF Viewer"}</span>
          <img src={theme === "dark" ? logoDark : logo} alt="Induz logo" className="pdf-drawer-logo" />
        </div>
      }
      height="100vh"
      closable={true}
      mockClass="pdf-fullscreen-drawer"
    >
      <div className="pdf-viewer-container" ref={containerRef}>
        {loading && (
          <div className="pdf-loading">
            <p>Loading PDF...</p>
          </div>
        )}
        {error && (
          <div className="pdf-error">
            <p>{error}</p>
          </div>
        )}
        {pdfDoc && !loading && !error && (
          <>
            {isSlidesMode && !isEditMode ? (
              <SlidesView
                pdfDoc={pdfDoc}
                currentPage={currentPage}
                totalPages={totalPages}
                rotation={rotation}
                onPageChange={handleSlidesPageChange}
                onClose={toggleSlidesMode}
              />
            ) : (
              <div className="pdf-viewer-wrapper">
                <Toolbar
                  currentPage={currentPage}
                  totalPages={totalPages}
                  scale={scale}
                  onZoomIn={zoomIn}
                  onZoomOut={zoomOut}
                  onResetZoom={resetZoom}
                  onFitWidth={fitWidth}
                  onRotateLeft={rotateLeft}
                  onRotateRight={rotateRight}
                  onToggleSidebar={toggleSidebar}
                  onDownload={downloadPdf}
                  onPrint={printPdf}
                  onSlides={toggleSlidesMode}
                  onComment={handleToggleCommentsSidebar}
                  onEdit={handleToggleEditMode}
                  onFullscreen={enterFullscreen}
                  isCommentMode={isCommentsSidebarOpen}
                  isEditMode={isEditMode}
                  isPreparingEditMode={isPreparingEditMode}
                  theme={theme}
                  disableZoomIn={scale >= fitScale}
                  disableZoomOut={scale <= 0.4}
                  onClose={handlePDFViewerClose}
                />
                {isEditMode && (
                  <PDFDrawToolbar
                    onSave={handleDrawSave}
                    onClose={handleDrawClose}
                    onToolChange={handleDrawToolChange}
                    selectedTool={selectedDrawTool}
                    selectedColor={selectedDrawColor}
                    onColorChange={handleDrawColorChange}
                    brushSize={brushSize}
                    onBrushSizeChange={handleBrushSizeChange}
                    onUndo={handleUndo}
                    onRedo={handleRedo}
                    canUndo={canUndo}
                    canRedo={canRedo}
                    theme={theme}
                    isSaving={isSaving}
                    saveProgress={saveProgress}
                    hasChanges={hasUnsavedChanges}
                  />
                )}
                <div
                  className={`pdf-canvas-container ${isSidebarOpen ? "with-sidebar" : "without-sidebar"} ${isCommentMode ? "is-commenting" : ""}`}
                  style={{
                    gridTemplateColumns: `${isSidebarOpen ? "160px" : ""} 1fr ${isCommentsSidebarOpen ? `auto ${sidebarWidth}px` : "0px 0px"}`
                  }}
                >
                  {isSidebarOpen && (
                    <Thumbnails
                      totalPages={totalPages}
                      currentPage={currentPage}
                      onSelectPage={scrollToPage}
                      thumbRefs={thumbRefs}
                      isSidebarOpen={isSidebarOpen}
                      listRef={thumbsContainerRef}
                    />
                  )}
                  <div
                    className="pdf-pages"
                    ref={pagesContainerRef}
                    onMouseMove={handleMouseMove}
                  >
                    {Array.from({ length: totalPages }).map((_, idx) => (
                      <div key={`${renderKey}-${idx}`} className="pdf-page" ref={pageRefs.current[idx]}>
                        <canvas
                          ref={canvasRefs.current[idx]}
                          className="pdf-canvas"
                        />

                        {/* Current Selection Area while drawing */}
                        {isDrawing && selectionStart && selectionEnd && selectionStart.page === idx + 1 && (() => {
                          const minX = Math.min(selectionStart.x, selectionEnd.x);
                          const minY = Math.min(selectionStart.y, selectionEnd.y);
                          const w = Math.abs(selectionStart.x - selectionEnd.x);
                          const h = Math.abs(selectionStart.y - selectionEnd.y);

                          let x = minX;
                          let y = minY;
                          let width = w;
                          let height = h;

                          // Transform coordinates based on rotation
                          if (rotation === 90) {
                            x = 1 - minY - h;
                            y = minX;
                            width = h;
                            height = w;
                          } else if (rotation === 180) {
                            x = 1 - minX - w;
                            y = 1 - minY - h;
                          } else if (rotation === 270) {
                            x = minY;
                            y = 1 - minX - w;
                            width = h;
                            height = w;
                          }

                          return (
                            <div
                              className="pdf-selection-area"
                              style={{
                                left: `${x * 100}%`,
                                top: `${y * 100}%`,
                                width: `${width * 100}%`,
                                height: `${height * 100}%`,
                              }}
                            />
                          );
                        })()}

                        {/* Markers for existing comments - respect status filter */}
                        {comments
                          .filter((c) => c.page === idx + 1 && (c.state || "active") === "active")
                          .map((c) => {
                            // Coordinates are stored as percentages (0-1) relative to the canvas
                            // When the canvas rotates, we need to transform coordinates
                            // The coordinates are relative to the canvas at the time they were created
                            // We need to transform them to match the current rotation
                            let x = c.x;
                            let y = c.y;
                            let w = c.width || 0.05;
                            let h = c.height || 0.02;

                            // Transform coordinates based on rotation
                            // For 90° clockwise: (x, y) -> (1-y-h, x) with swapped dimensions
                            // For 180°: (x, y) -> (1-x-w, 1-y-h)
                            // For 270° clockwise: (x, y) -> (y, 1-x-w) with swapped dimensions
                            if (rotation === 90) {
                              // 90° clockwise: top-left (x, y) becomes (1-y-h, x)
                              const newX = 1 - c.y - (c.height || 0.02);
                              const newY = c.x;
                              x = newX;
                              y = newY;
                              w = c.height || 0.02;
                              h = c.width || 0.05;
                            } else if (rotation === 180) {
                              // 180°: (x, y) -> (1-x-w, 1-y-h)
                              x = 1 - c.x - w;
                              y = 1 - c.y - h;
                            } else if (rotation === 270) {
                              // 270° clockwise: (x, y) -> (y, 1-x-w)
                              x = c.y;
                              y = 1 - c.x - w;
                              w = c.height || 0.02;
                              h = c.width || 0.05;
                            }

                            return (
                              <div
                                key={c.id}
                                className={`pdf-comment-marker area ${highlightedCommentId === c.id ? "highlighted" : ""}`}
                                style={{
                                  left: `${x * 100}%`,
                                  top: `${y * 100}%`,
                                  width: `${w * 100}%`,
                                  height: `${h * 100}%`,
                                }}
                                onMouseEnter={(e) => {
                                  const rect = e.currentTarget.getBoundingClientRect();
                                  setHoveredComment({
                                    comment: c,
                                    bounds: { x: rect.left, y: rect.bottom }
                                  });
                                }}
                                onMouseLeave={() => setHoveredComment(null)}
                              />
                            );
                          })}

                        {/* Pending comment marker */}
                        {pendingComment && pendingComment.page === idx + 1 && (() => {
                          let x = pendingComment.x;
                          let y = pendingComment.y;
                          let w = pendingComment.width || 0.05;
                          let h = pendingComment.height || 0.02;

                          // Transform coordinates based on rotation
                          if (rotation === 90) {
                            x = 1 - pendingComment.y - (pendingComment.height || 0.02);
                            y = pendingComment.x;
                            w = pendingComment.height || 0.02;
                            h = pendingComment.width || 0.05;
                          } else if (rotation === 180) {
                            x = 1 - pendingComment.x - w;
                            y = 1 - pendingComment.y - h;
                          } else if (rotation === 270) {
                            x = pendingComment.y;
                            y = 1 - pendingComment.x - w;
                            w = pendingComment.height || 0.02;
                            h = pendingComment.width || 0.05;
                          }

                          return (
                            <div
                              className="pdf-comment-marker pending area"
                              style={{
                                left: `${x * 100}%`,
                                top: `${y * 100}%`,
                                width: `${w * 100}%`,
                                height: `${h * 100}%`,
                              }}
                            />
                          );
                        })()}
                      </div>
                    ))}
                    {/* Always render PDFDrawCanvas to preserve state, but control behavior with isDrawingMode prop */}
                    <PDFDrawCanvas
                      ref={drawCanvasRef}
                      pdfDoc={pdfDoc}
                      totalPages={totalPages}
                      isDrawingMode={isEditMode}
                      selectedTool={selectedDrawTool}
                      selectedColor={selectedDrawColor}
                      brushSize={brushSize}
                      pageRefs={pageRefs}
                      theme={theme}
                      onDrawingsChange={handleDrawingsChange}
                    />
                  </div>
                  <div
                    className={`pdf-sidebar-resizer ${isResizing ? "is-resizing" : ""}`}
                    onMouseDown={handleResizerMouseDown}
                    style={{ display: isCommentsSidebarOpen ? "block" : "none" }}
                  />
                  <div className="pdf-comments-sidebar" style={{ width: sidebarWidth, display: isCommentsSidebarOpen ? "flex" : "none" }}>
                    <div className="pdf-comments-sidebar-header">
                      <div className="pdf-comments-sidebar-header-content">
                        <span>
                          {(() => {
                            const selectedStatuses = [];
                            if (statusFilter.active) selectedStatuses.push("Active");
                            if (statusFilter.resolved) selectedStatuses.push("Resolved");
                            const statusText = selectedStatuses.length > 0
                              ? selectedStatuses.join(" & ")
                              : "Comments";
                            return `${statusText} (${displayedComments.length})`;
                          })()}
                        </span>
                        <div className="pdf-comments-sidebar-filter-container">
                          <button
                            className="pdf-comments-sidebar-add-comment-button"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!isEditMode) {
                                handleToggleCommentMode();
                              }
                            }}
                            title={isEditMode ? "Add comment disabled in draw mode" : (isCommentMode ? "Cancel Comment" : "Add comment")}
                            disabled={isEditMode}
                          >
                            {isCommentMode ? "Cancel" : "Add"}
                          </button>
                          <button
                            className="pdf-comments-sidebar-filter-button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setFilterMenuOpen(!filterMenuOpen);
                            }}
                            title="Filter comments"
                          >
                            <img
                              src={theme === "dark" ? filterIconDark : filterIcon}
                              alt="Filter"
                              style={{ width: "16px", height: "16px" }}
                            />
                          </button>
                          {filterMenuOpen && (
                            <div
                              className="pdf-comments-sidebar-filter-dropdown"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {/* Section 1: Pagewise / All Comments */}
                              <div className="pdf-filter-section">
                                <div className="pdf-filter-section-title">
                                  View
                                </div>
                                <label className="pdf-filter-radio-label">
                                  <input
                                    type="radio"
                                    name="commentFilter"
                                    value="pagewise"
                                    checked={commentFilter === "pagewise"}
                                    onChange={(e) => setCommentFilter(e.target.value)}
                                  />
                                  Pagewise
                                </label>
                                <label className="pdf-filter-radio-label">
                                  <input
                                    type="radio"
                                    name="commentFilter"
                                    value="all"
                                    checked={commentFilter === "all"}
                                    onChange={(e) => setCommentFilter(e.target.value)}
                                  />
                                  All Comments
                                </label>
                              </div>

                              {/* Section 2: Active / Resolved */}
                              <div className="pdf-filter-section">
                                <div className="pdf-filter-section-title">
                                  Status
                                </div>
                                <label className="pdf-filter-checkbox-label">
                                  <input
                                    type="checkbox"
                                    checked={statusFilter.active}
                                    onChange={(e) => {
                                      const newValue = e.target.checked;
                                      // Prevent unchecking if it's the last checked option
                                      if (!newValue && !statusFilter.resolved) {
                                        return; // Don't allow unchecking the last option
                                      }
                                      setStatusFilter(prev => ({ ...prev, active: newValue }));
                                    }}
                                  />
                                  Active Comments
                                </label>
                                <label className="pdf-filter-checkbox-label">
                                  <input
                                    type="checkbox"
                                    checked={statusFilter.resolved}
                                    onChange={(e) => {
                                      const newValue = e.target.checked;
                                      // Prevent unchecking if it's the last checked option
                                      if (!newValue && !statusFilter.active) {
                                        return; // Don't allow unchecking the last option
                                      }
                                      setStatusFilter(prev => ({ ...prev, resolved: newValue }));
                                    }}
                                  />
                                  Resolved Comments
                                </label>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    {isCommentMode && !pendingComment && (
                      <div className="pdf-comments-sidebar-instruction">
                        <div className="pdf-comments-sidebar-instruction-content">
                          <span className="pdf-comments-sidebar-instruction-icon">ℹ️</span>
                          <span className="pdf-comments-sidebar-instruction-text">Select an area in PDF to add Comment</span>
                        </div>
                      </div>
                    )}
                    {pendingComment && (
                      <div className="pdf-comment-sidebar-panel" style={{ padding: "12px", borderBottom: `1px solid ${theme === "dark" ? "var(--border-dark, #333)" : "var(--border-color, #eaeaea)"}`, backgroundColor: theme === "dark" ? "var(--bg-dark-secondary, #2a2a2a)" : "var(--bg-color-secondary, #fafafa)" }}>
                        <div className="pdf-comment-panel-header" style={{ marginBottom: "8px", fontWeight: "600", fontSize: "14px", color: theme === "dark" ? "#eee" : "inherit" }}>
                          <span>Add comment</span>
                        </div>
                        <div style={{ position: "relative" }}>
                          <textarea
                            ref={commentTextareaRef}
                            className="pdf-comment-panel-input"
                            style={{
                              width: "100%",
                              minHeight: "60px",
                              padding: "8px",
                              border: `1px solid ${theme === "dark" ? "#444" : "#ccc"}`,
                              borderRadius: "4px",
                              resize: "none",
                              fontSize: "13px",
                              backgroundColor: theme === "dark" ? "#333" : "#fff",
                              color: theme === "dark" ? "#eee" : "#333"
                            }}
                            value={commentText}
                            onChange={(e) => handleInputChangeWithMentions(e, "comment")}
                            placeholder="Type your comment... Use @ to mention"
                            autoFocus
                          />
                          {renderMentionDropdown("comment")}
                        </div>
                        <div className="pdf-comment-panel-sub" style={{ fontSize: "11px", color: theme === "dark" ? "#999" : "var(--text-color-secondary, #666)", marginTop: "4px" }}>
                          Page {pendingComment.page}
                        </div>
                        <div className="pdf-comment-panel-actions" style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "8px" }}>
                          <button
                            className="pdf-comment-btn secondary"
                            style={{
                              padding: "4px 8px",
                              fontSize: "12px",
                              cursor: "pointer",
                              border: `1px solid ${theme === "dark" ? "#555" : "#ccc"}`,
                              background: "transparent",
                              borderRadius: "4px",
                              color: theme === "dark" ? "#ddd" : "#333"
                            }}
                            onClick={handleCancelComment}
                          >
                            Cancel
                          </button>
                          <button
                            className="pdf-comment-btn primary"
                            style={{
                              padding: "4px 12px",
                              fontSize: "12px",
                              cursor: "pointer",
                              background: "var(--primary-color, #67308f)",
                              color: "white",
                              border: "none",
                              borderRadius: "4px",
                              opacity: !commentText.trim() ? 0.6 : 1
                            }}
                            onClick={handleSaveComment}
                            disabled={!commentText.trim()}
                          >
                            Save
                          </button>
                        </div>
                      </div>
                    )}
                    {displayedComments.length > 0 ? (
                      <div className="pdf-comments-sidebar-list">
                        {displayedComments.map((c) => {
                          const emailInitial = c.email ? c.email.charAt(0).toUpperCase() : "?";
                          const timeAgo = c.createdAt ? getTimeAgo(new Date(c.createdAt)) : "";
                          return (
                            <div
                              key={c.id}
                              data-comment-id={c.id}
                              className="pdf-comments-sidebar-item"
                              onClick={() => scrollToCommentLocation(c)}
                            >
                              <div className="pdf-comments-sidebar-avatar">
                                <Avatar initial={emailInitial} size={32} />
                              </div>
                              <div className="pdf-comments-sidebar-content">
                                <div className="pdf-comments-sidebar-header-row">
                                  <div className="pdf-comments-sidebar-email-container">
                                    <div className="pdf-comments-sidebar-email" title={c.email || "Unknown"}>{c.email || "Unknown"}</div>
                                    {c.state === "resolved" && (
                                      <span className="pdf-comments-sidebar-resolved-indicator">Resolved</span>
                                    )}
                                  </div>
                                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                    {c.state !== "resolved" && canEditComment(c.email) && (
                                      <button
                                        className="pdf-comments-sidebar-resolve-button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleResolveComment(c.id);
                                        }}
                                        title="Mark as resolved"
                                      >
                                        <img
                                          src={theme === "dark" ? markResolvedIconDark : markResolvedIcon}
                                          alt="Mark as resolved"
                                        />
                                      </button>
                                    )}
                                    {/* Show menu if user can delete, can edit active comments, or can reopen resolved comments */}
                                    {(canDeleteComment(c.email) || (c.state !== "resolved" && canEditComment(c.email)) || (c.state === "resolved" && canEditComment(c.email))) && (
                                      <div className="pdf-comments-sidebar-menu-container">
                                        <button
                                          className="pdf-comments-sidebar-menu-button"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setCommentMenuOpen(commentMenuOpen === c.id ? null : c.id);
                                          }}
                                          title="Options"
                                        >
                                          ⋯
                                        </button>
                                        {commentMenuOpen === c.id && (
                                          <div className="pdf-comments-sidebar-menu-dropdown">
                                            {/* Edit option - only show for active comments */}
                                            {c.state !== "resolved" && canEditComment(c.email) && (
                                              <button
                                                className="pdf-comments-sidebar-menu-item"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleStartEdit(c.id, c.text);
                                                  setCommentMenuOpen(null);
                                                }}
                                              >
                                                Edit
                                              </button>
                                            )}
                                            {/* Reopen option - only show for resolved comments owned by the user */}
                                            {c.state === "resolved" && canEditComment(c.email) && (
                                              <button
                                                className="pdf-comments-sidebar-menu-item"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleReopenComment(c.id);
                                                  setCommentMenuOpen(null);
                                                }}
                                              >
                                                Reopen
                                              </button>
                                            )}
                                            {/* Delete option - show for both active and resolved comments if user has permission */}
                                            {canDeleteComment(c.email) && (
                                              <button
                                                className="pdf-comments-sidebar-menu-item"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleDeleteComment(c.id);
                                                  setCommentMenuOpen(null);
                                                }}
                                              >
                                                Delete
                                              </button>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>
                                {editingCommentId === c.id && c.state !== "resolved" ? (
                                  <div className="pdf-comments-sidebar-edit-input">
                                    <textarea
                                      className="pdf-comments-sidebar-edit-textarea"
                                      style={{ resize: "none" }}
                                      value={editCommentText}
                                      onChange={(e) => handleInputChangeWithMentions(e, "edit", c.id)}
                                      placeholder="Edit your comment... Use @ to mention"
                                      autoFocus
                                      onFocus={(e) => {
                                        const val = e.target.value;
                                        e.target.value = "";
                                        e.target.value = val;
                                      }}
                                    />
                                    {renderMentionDropdown("edit", c.id)}
                                    <div className="pdf-comments-sidebar-edit-actions">
                                      <button
                                        className="pdf-comment-btn secondary"
                                        onClick={handleCancelEdit}
                                      >
                                        Cancel
                                      </button>
                                      <button
                                        className="pdf-comment-btn primary"
                                        onClick={() => handleSaveEdit(c.id)}
                                        disabled={!editCommentText.trim() || !canEditComment(c.email)}
                                      >
                                        Save
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="pdf-comments-sidebar-text">
                                    {c.text.length > 150 && !expandedComments[c.id] ? (
                                      <>
                                        {renderTextWithMentions(c.text.substring(0, 150))}...
                                        <button
                                          className="pdf-comments-sidebar-more-btn"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setExpandedComments(prev => ({ ...prev, [c.id]: true }));
                                          }}
                                        >
                                          Read more
                                        </button>
                                      </>
                                    ) : (
                                      <>
                                        {renderTextWithMentions(c.text)}
                                        {c.text.length > 150 && (
                                          <button
                                            className="pdf-comments-sidebar-more-btn"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setExpandedComments(prev => ({ ...prev, [c.id]: false }));
                                            }}
                                          >
                                            Show less
                                          </button>
                                        )}
                                      </>
                                    )}
                                  </div>
                                )}
                                <div className="pdf-comments-sidebar-footer">
                                  <span className="pdf-comments-sidebar-page">Page {c.page}</span>
                                  {timeAgo && <span className="pdf-comments-sidebar-time">{timeAgo}</span>}
                                </div>

                                {/* Replies Section */}
                                {c.replies && c.replies.length > 0 && (
                                  <div className="pdf-comments-sidebar-replies">
                                    {c.replies.map((reply, idx) => {
                                      const replyEmailInitial = reply.email ? reply.email.charAt(0).toUpperCase() : "?";
                                      const replyTimeAgo = reply.createdAt ? getTimeAgo(new Date(reply.createdAt)) : "";
                                      return (
                                        <div key={idx} className="pdf-comments-sidebar-reply-item">
                                          <div className="pdf-comments-sidebar-avatar">
                                            <Avatar initial={replyEmailInitial} size={24} />
                                          </div>
                                          <div className="pdf-comments-sidebar-content">
                                            <div className="pdf-comments-sidebar-header-row">
                                              <div className="pdf-comments-sidebar-email" title={reply.email || "Unknown"}>
                                                {reply.email || "Unknown"}
                                              </div>
                                              {c.state !== "resolved" && (canEditComment(reply.email) || canDeleteComment(reply.email)) && (
                                                <div className="pdf-comments-sidebar-menu-container">
                                                  <button
                                                    className="pdf-comments-sidebar-reply-menu-button"
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      const menuKey = `${c.id}-${idx}`;
                                                      setReplyMenuOpen(replyMenuOpen === menuKey ? null : menuKey);
                                                    }}
                                                    title="Options"
                                                  >
                                                    ⋯
                                                  </button>
                                                  {replyMenuOpen === `${c.id}-${idx}` && (
                                                    <div className="pdf-comments-sidebar-reply-menu-dropdown">
                                                      {canEditComment(reply.email) && (
                                                        <button
                                                          className="pdf-comments-sidebar-menu-item"
                                                          onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleStartEditReply(c.id, idx, reply.text);
                                                          }}
                                                        >
                                                          Edit
                                                        </button>
                                                      )}
                                                      {canDeleteComment(reply.email) && (
                                                        <button
                                                          className="pdf-comments-sidebar-menu-item"
                                                          onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDeleteReply(c.id, idx);
                                                          }}
                                                        >
                                                          Delete
                                                        </button>
                                                      )}
                                                    </div>
                                                  )}
                                                </div>
                                              )}
                                            </div>
                                            {editingReply && editingReply.commentId === c.id && editingReply.replyIndex === idx ? (
                                              <div className="pdf-comments-sidebar-edit-reply-input">
                                                <textarea
                                                  className="pdf-comments-sidebar-edit-textarea"
                                                  style={{ resize: "none" }}
                                                  value={editReplyText}
                                                  onChange={(e) => handleInputChangeWithMentions(e, "editReply", c.id)}
                                                  placeholder="Edit your reply... Use @ to mention"
                                                  autoFocus
                                                  onFocus={(e) => {
                                                    const val = e.target.value;
                                                    e.target.value = "";
                                                    e.target.value = val;
                                                  }}
                                                />
                                                {renderMentionDropdown("editReply", c.id)}
                                                <div className="pdf-comments-sidebar-edit-actions">
                                                  <button
                                                    className="pdf-comment-btn secondary"
                                                    onClick={handleCancelEditReply}
                                                  >
                                                    Cancel
                                                  </button>
                                                  <button
                                                    className="pdf-comment-btn primary"
                                                    onClick={() => handleSaveEditReply(c.id, idx)}
                                                    disabled={!editReplyText.trim() || !canEditComment(reply.email)}
                                                  >
                                                    Save
                                                  </button>
                                                </div>
                                              </div>
                                            ) : (
                                              <div className="pdf-comments-sidebar-text">
                                                {reply.text.length > 100 && !expandedComments[`${c.id}-reply-${idx}`] ? (
                                                  <>
                                                    {renderTextWithMentions(reply.text.substring(0, 100))}...
                                                    <button
                                                      className="pdf-comments-sidebar-more-btn"
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        setExpandedComments(prev => ({ ...prev, [`${c.id}-reply-${idx}`]: true }));
                                                      }}
                                                    >
                                                      Read more
                                                    </button>
                                                  </>
                                                ) : (
                                                  <>
                                                    {renderTextWithMentions(reply.text)}
                                                    {reply.text.length > 100 && (
                                                      <button
                                                        className="pdf-comments-sidebar-more-btn"
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          setExpandedComments(prev => ({ ...prev, [`${c.id}-reply-${idx}`]: false }));
                                                        }}
                                                      >
                                                        Show less
                                                      </button>
                                                    )}
                                                  </>
                                                )}
                                              </div>
                                            )}
                                            {replyTimeAgo && (
                                              <div className="pdf-comments-sidebar-time">{replyTimeAgo}</div>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}

                                {/* Reply Input - only show for active comments */}
                                {c.state !== "resolved" && (
                                  <>
                                    {replyingToCommentId === c.id ? (
                                      <div className="pdf-comments-sidebar-reply-input">
                                        <textarea
                                          className="pdf-comments-sidebar-reply-textarea"
                                          style={{ resize: "none" }}
                                          value={replyText}
                                          onChange={(e) => handleInputChangeWithMentions(e, "reply", c.id)}
                                          placeholder="Write a reply... Use @ to mention"
                                          autoFocus
                                        />
                                        {renderMentionDropdown("reply", c.id)}
                                        <div className="pdf-comments-sidebar-reply-actions">
                                          <button
                                            className="pdf-comment-btn secondary"
                                            onClick={handleCancelReply}
                                          >
                                            Cancel
                                          </button>
                                          <button
                                            className="pdf-comment-btn primary"
                                            onClick={() => handleSaveReply(c.id)}
                                            disabled={!replyText.trim()}
                                          >
                                            Reply
                                          </button>
                                        </div>
                                      </div>
                                    ) : (
                                      <button
                                        className="pdf-comments-sidebar-reply-button"
                                        onClick={() => handleStartReply(c.id)}
                                      >
                                        Reply
                                      </button>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="pdf-comments-sidebar-empty">
                        No comments
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Hovered Comment Tooltip */}
      {hoveredComment && (
        <div
          style={{
            position: "fixed",
            left: hoveredComment.bounds.x,
            top: hoveredComment.bounds.y + 4,
            zIndex: 1000,
            backgroundColor: theme === "dark" ? "var(--bg-dark-secondary, #2a2a2a)" : "white",
            border: `1px solid ${theme === "dark" ? "#444" : "#e5e7eb"}`,
            borderRadius: "4px",
            padding: "8px",
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
            maxWidth: "250px",
            pointerEvents: "none",
            color: theme === "dark" ? "#eee" : "inherit"
          }}
        >
          <div style={{ fontSize: "14px", fontWeight: "500", color: theme === "dark" ? "#60a5fa" : "#2563eb", marginBottom: "4px" }}>
            {hoveredComment.comment.email || "Unknown"}
          </div>
          <div
            style={{
              fontSize: "14px",
              color: theme === "dark" ? "#ccc" : "#374151",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              overflowWrap: "anywhere"
            }}
          >
            {hoveredComment.comment.text}
          </div>
        </div>
      )}

      {/* Confirmation modal for unsaved changes */}
      {showCloseConfirm && (
        <Modal
          showHeader={true}
          handleCancel={handleCancelCloseConfirm}
          handleSave={handleConfirmCloseWithoutSave}
          submitBtnText="Discard"
          cancelBtnText="Cancel"
        >
          <div style={{ padding: "20px", textAlign: "center", backgroundColor: theme === "dark" ? "var(--bg-dark, #1f1f1f)" : "var(--bg-light, #ffffff)" }}>
            <h3 style={{ marginBottom: "16px", fontSize: "18px", fontWeight: "600", color: theme === "dark" ? "#ffffff" : "#000000" }}>
              Unsaved Changes
            </h3>
            <p style={{ marginBottom: "24px", color: theme === "dark" ? "#bbbbbb" : "var(--text-grey-medium, #666)" }}>
              You have unsaved drawings. Are you sure you want to close without saving?
            </p>
          </div>
        </Modal>
      )}
    </Drawer>
  );
};

export default PDFViewer;

